import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Users, Plus, PencilSimple, Trash, MagnifyingGlass, CurrencyEur, EnvelopeSimple, FileText, Paperclip, PaperPlaneTilt, X, DownloadSimple, CheckSquare, Eye, CalendarPlus, Link as LinkIcon, Copy, CheckCircle, WhatsappLogo, Tag, ClipboardText } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from './formatters';
import { Checkbox } from '../../components/ui/checkbox';
import { PLANNER_DAYS, TIME_SLOTS } from '../teacher/constants';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const QUILL_MODULES_MAIL = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const EMAIL_LABELS = ['Padre', 'Madre', 'Tutore', 'Alunno', 'Altro'];

const calcAge = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const now = new Date();
  const age = now.getFullYear() - y - ((now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d)) ? 1 : 0);
  return Number.isFinite(age) ? age : null;
};

export function StudentsSection({ teachers, anno }) {
  const DAY_NAMES = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
  const STATUS_FLAGS_ORDER = ['i', 'r1', 'r2', 'r3'];
  const STATUS_FLAG_LABELS = { i: 'I', r1: 'R1', r2: 'R2', r3: 'R3' };
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [teacherId, setTeacherId] = useState('');
  const [search, setSearch] = useState('');
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [paymentSummary, setPaymentSummary] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', phone2: '', data_nascita: '', emails: [] });

  const [showPayments, setShowPayments] = useState(false);
  const [paymentsData, setPaymentsData] = useState([]);
  const [paymentsTeacherFilter, setPaymentsTeacherFilter] = useState('all');
  const [paymentsTotals, setPaymentsTotals] = useState({ total_addebiti_listino: 0, total_dovuto_addebiti: 0, total_pagato_addebiti: 0, total_da_pagare_addebiti: 0 });
  const [paymentsAvailableYears, setPaymentsAvailableYears] = useState([]);
  const [paymentsYear, setPaymentsYear] = useState('');
  const [paymentsStudent, setPaymentsStudent] = useState(null);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [selectedPaymentIds, setSelectedPaymentIds] = useState([]);
  const [downloadingSelectivePdf, setDownloadingSelectivePdf] = useState(false);

  const [emailTemplates, setEmailTemplates] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailStudent, setEmailStudent] = useState(null);
  const [emailMode, setEmailMode] = useState('free');
  const [emailForm, setEmailForm] = useState({ subject: '', body: '', template_id: '', selectedRecipients: [] });
  const [emailSelectedPaymentIds, setEmailSelectedPaymentIds] = useState([]);
  const [emailFromPayments, setEmailFromPayments] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const TEST_EMAIL = 'lorenzo@ifolegati.it';
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailStep, setEmailStep] = useState('compose');
  const [extraFiles, setExtraFiles] = useState([]);

  // Mass email
  const [selectedIds, setSelectedIds] = useState([]);
  const [showMassEmail, setShowMassEmail] = useState(false);

  // Booking invite (link personalizzato)
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingStudent, setBookingStudent] = useState(null);
  const [bookingUrl, setBookingUrl] = useState('');
  const [bookingLinks, setBookingLinks] = useState([]);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingSending, setBookingSending] = useState(false);
  const [enrolledFilter, setEnrolledFilter] = useState('all'); // 'all' | 'enrolled' | 'not-enrolled'
  const [bulkBookingSending, setBulkBookingSending] = useState(false);
  const [massEmailForm, setMassEmailForm] = useState({ template_id: '' });
  const [sendingMass, setSendingMass] = useState(false);
  const [massPreview, setMassPreview] = useState(null);
  const [massEditedSubject, setMassEditedSubject] = useState('');
  const [massEditedBody, setMassEditedBody] = useState('');
  const refreshMassPreview = async (subj, bod, templateId, studentsList) => {
    const firstWithEmail = studentsList.find(s => s.emails?.some(em => em.address));
    if (!firstWithEmail || !templateId) { setMassPreview(null); return; }
    try {
      const res = await api.previewEmail({ student_id: firstWithEmail.id, template_id: templateId, custom_subject: subj, custom_body: bod, source_year: anno, target_year: anno ? anno + 1 : undefined });
      setMassPreview({ subject: res.data.subject, body: res.data.body, sampleStudent: firstWithEmail.name });
    } catch (err) { /* ignore */ }
  };

  // Genera Presenze (spostato dall'orario) — modal con selezione lezioni + intervallo date
  const [showGenAttModal, setShowGenAttModal] = useState(false);
  const [genAttStudent, setGenAttStudent] = useState(null);
  const [genAttSelectedLessonIds, setGenAttSelectedLessonIds] = useState([]);
  const [genAttForm, setGenAttForm] = useState({ date_from: '', date_to: '' });
  const [genAttLoading, setGenAttLoading] = useState(false);
  const [genAttHolidays, setGenAttHolidays] = useState({ custom: [], national: [] });
  const [genAttHolidaysLoading, setGenAttHolidaysLoading] = useState(false);

  const loadGenAttHolidays = async () => {
    if (!anno) return;
    setGenAttHolidaysLoading(true);
    try {
      const res = await api.getAdminHolidays(anno);
      setGenAttHolidays(res.data || { custom: [], national: [] });
    } catch { setGenAttHolidays({ custom: [], national: [] }); }
    finally { setGenAttHolidaysLoading(false); }
  };

  const openGenAttModal = (student) => {
    setGenAttStudent(student);
    const lessonsInYear = (student.lessons || []).filter(l => !anno || !l.anno || l.anno === anno);
    setGenAttSelectedLessonIds(lessonsInYear.map(l => l.id));
    const yr = anno || new Date().getFullYear();
    setGenAttForm({ date_from: `${yr}-09-01`, date_to: `${yr + 1}-06-30` });
    setShowGenAttModal(true);
    loadGenAttHolidays();
  };

  // Preview conteggio presenze: calcola quante ne verrebbero create nel range
  // per le lezioni selezionate, distinguendo attive/festive in base al calendario.
  const genAttPreview = useMemo(() => {
    if (!showGenAttModal || !genAttStudent || !genAttForm.date_from || !genAttForm.date_to) {
      return null;
    }
    const from = new Date(genAttForm.date_from + 'T00:00:00');
    const to = new Date(genAttForm.date_to + 'T00:00:00');
    if (isNaN(from) || isNaN(to) || from > to) return null;
    // Set delle date festive (custom + national), come stringhe ISO
    const holidaysSet = new Set([
      ...(genAttHolidays.custom || []).map(h => h.date),
      ...(genAttHolidays.national || []).map(h => h.date),
    ]);
    const lessonsById = Object.fromEntries((genAttStudent.lessons || []).map(l => [l.id, l]));
    const selectedLessons = genAttSelectedLessonIds.map(id => lessonsById[id]).filter(Boolean);
    if (selectedLessons.length === 0) return { total: 0, active: 0, holiday: 0, perLesson: [] };

    let totalActive = 0;
    let totalHoliday = 0;
    const perLesson = [];
    for (const l of selectedLessons) {
      let active = 0;
      let holiday = 0;
      const cur = new Date(from);
      while (cur <= to) {
        // app_day: 0=Mon..6=Sun; JS: 0=Sun..6=Sat
        const jsDay = cur.getDay();
        const appDay = (jsDay + 6) % 7;
        if (appDay === l.day) {
          const iso = cur.toISOString().slice(0, 10);
          if (holidaysSet.has(iso)) holiday += 1;
          else active += 1;
        }
        cur.setDate(cur.getDate() + 1);
      }
      totalActive += active;
      totalHoliday += holiday;
      perLesson.push({ id: l.id, teacher_name: l.teacher_name, day: l.day, start_time: l.start_time, active, holiday, total: active + holiday });
    }
    return { total: totalActive + totalHoliday, active: totalActive, holiday: totalHoliday, perLesson };
  }, [showGenAttModal, genAttStudent, genAttSelectedLessonIds, genAttForm.date_from, genAttForm.date_to, genAttHolidays]);

  const toggleGenAttLesson = (lid) => {
    setGenAttSelectedLessonIds(prev => prev.includes(lid) ? prev.filter(x => x !== lid) : [...prev, lid]);
  };

  const handleSubmitGenAtt = async (e) => {
    e.preventDefault();
    if (!genAttStudent) return;
    if (!genAttSelectedLessonIds.length) { toast.error('Seleziona almeno una lezione'); return; }
    if (!genAttForm.date_from || !genAttForm.date_to) { toast.error('Specifica le date'); return; }
    setGenAttLoading(true);
    const results = { created: 0, skipped: 0, holidays: 0, errors: 0 };
    try {
      const lessonsById = Object.fromEntries((genAttStudent.lessons || []).map(l => [l.id, l]));
      for (const lid of genAttSelectedLessonIds) {
        const lesson = lessonsById[lid];
        if (!lesson || !lesson.teacher_id) { results.errors += 1; continue; }
        try {
          const res = await api.generateAttendanceFromLesson(lesson.teacher_id, lid, {
            date_from: genAttForm.date_from,
            date_to: genAttForm.date_to,
            anno,
          });
          const d = res.data || {};
          results.created += d.created || 0;
          results.skipped += d.skipped_existing || 0;
          results.holidays += d.holidays || 0;
        } catch {
          results.errors += 1;
        }
      }
      toast.success(`Presenze create: ${results.created}${results.holidays ? ` (${results.holidays} festive)` : ''}${results.skipped ? ` · ${results.skipped} saltate` : ''}${results.errors ? ` · ${results.errors} errori` : ''}`);
      setShowGenAttModal(false);
      setGenAttStudent(null);
      await loadStudents();
    } finally {
      setGenAttLoading(false);
    }
  };

  // Assegna Orario (nuova prenotazione da griglia pubblica)
  const [showAssignSchedule, setShowAssignSchedule] = useState(false);
  const [assignStudent, setAssignStudent] = useState(null);
  const [assignForm, setAssignForm] = useState({ teacher_id: '', durata: 30, is_prova: false, giorno: -1, ora_inizio: '' });
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignAvailability, setAssignAvailability] = useState([]);
  const [assignLoadingSlots, setAssignLoadingSlots] = useState(false);

  // Tariffe per lezione (listino_id per ogni lezione dell'alunno)
  const [tariffeStudent, setTariffeStudent] = useState(null);
  const [tariffeLessons, setTariffeLessons] = useState([]);
  const [tariffeLoading, setTariffeLoading] = useState(false);
  const [tariffeAllListini, setTariffeAllListini] = useState([]);
  const [studentsListinoMap, setStudentsListinoMap] = useState({}); // studentId -> bool

  const openAssignSchedule = (student, preferredTeacherId = '') => {
    setAssignStudent(student);
    setAssignForm({ teacher_id: preferredTeacherId || '', durata: 30, is_prova: false, giorno: -1, ora_inizio: '' });
    setAssignAvailability([]);
    setShowAssignSchedule(true);
    if (preferredTeacherId) loadAssignAvailability(preferredTeacherId);
  };

  const loadAssignAvailability = async (tid) => {
    if (!tid) { setAssignAvailability([]); return; }
    setAssignLoadingSlots(true);
    try {
      const res = await api.getPublicAvailability(tid, anno);
      setAssignAvailability(res.data || []);
    } catch { setAssignAvailability([]); }
    finally { setAssignLoadingSlots(false); }
  };

  const handleAssignTeacherChange = (tid) => {
    setAssignForm(prev => ({ ...prev, teacher_id: tid, giorno: -1, ora_inizio: '' }));
    loadAssignAvailability(tid);
  };

  // === Tariffe per Lezione (assegnazione listino per ogni lezione) ===
  const openTariffe = async (student) => {
    setTariffeStudent(student);
    setTariffeLoading(true);
    try {
      const targetYear = anno ? anno + 1 : undefined;
      const [lRes, lstRes] = await Promise.all([
        api.getStudentLessonsWithListino(student.id, anno),
        api.getListini(targetYear),
      ]);
      setTariffeLessons(lRes.data || []);
      let lis = lstRes.data || [];
      if (lis.length === 0) {
        try { const all = await api.getListini(); lis = all.data || []; } catch { /* ignore */ }
      }
      setTariffeAllListini(lis);
    } catch { toast.error('Errore caricamento lezioni'); }
    finally { setTariffeLoading(false); }
  };

  const updateTariffaLesson = async (lessonId, listinoId) => {
    try {
      await api.setLessonListino(lessonId, listinoId);
      const updatedLessons = tariffeLessons.map(l => l.id === lessonId ? { ...l, listino_id: listinoId, listino: tariffeAllListini.find(x => x.id === listinoId) } : l);
      setTariffeLessons(updatedLessons);
      if (tariffeStudent) {
        const allSet = updatedLessons.length > 0 && updatedLessons.every(l => l.listino_id);
        setStudentsListinoMap(prev => ({ ...prev, [tariffeStudent.id]: allSet }));
      }
    } catch { toast.error('Errore aggiornamento listino'); }
  };

  const loadStudentsListinoStatus = useCallback(async (list) => {
    const map = {};
    for (const s of (list || []).filter(s => (s.emails || []).some(em => em.address))) {
      try {
        const res = await api.getStudentLessonsWithListino(s.id, anno);
        const lessons = res.data || [];
        map[s.id] = lessons.length > 0 && lessons.every(l => l.listino_id);
      } catch { map[s.id] = false; }
    }
    setStudentsListinoMap(map);
  }, [anno]);

  const assignCanSlotFit = (daySlots, startIdx, slotsNeeded) => {
    for (let i = 0; i < slotsNeeded; i++) {
      if (!daySlots[startIdx + i] || !daySlots[startIdx + i].available) return false;
    }
    return true;
  };

  const handleAssignSlotClick = (dayIdx, time) => {
    setAssignForm(prev => ({ ...prev, giorno: dayIdx, ora_inizio: time }));
  };

  const handleSaveAssignSchedule = async () => {
    if (!assignForm.teacher_id) { toast.error('Seleziona un insegnante'); return; }
    if (assignForm.giorno < 0 || !assignForm.ora_inizio) { toast.error('Seleziona uno slot orario'); return; }
    if (!assignStudent) return;
    const teacher = teachers.find(t => t.id === assignForm.teacher_id);
    if (!teacher) { toast.error('Insegnante non trovato'); return; }
    const primaryEmail = (assignStudent.emails || []).find(em => em.address)?.address || '';
    if (!primaryEmail) { toast.error("L'alunno non ha un'email principale"); return; }
    setAssignSaving(true);
    try {
      await api.createBooking({
        nome: assignStudent.name,
        telefono: assignStudent.phone || '',
        email: primaryEmail,
        anno_accademico: anno || new Date().getFullYear(),
        strumento: teacher.instrument || '',
        teacher_id: teacher.id,
        teacher_name: teacher.name,
        durata: assignForm.durata,
        giorno: assignForm.giorno,
        ora_inizio: assignForm.ora_inizio,
        is_prova: !!assignForm.is_prova,
      });
      toast.success(`Prenotazione creata per ${assignStudent.name.toUpperCase()} con ${teacher.name.toUpperCase()}`);
      setShowAssignSchedule(false);
      setAssignStudent(null);
      loadStudents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella creazione della prenotazione');
    } finally { setAssignSaving(false); }
  };

  const loadStudents = useCallback(async () => {
    setLoading(true);
    try {
      const tid = teacherId && teacherId !== 'all' ? teacherId : undefined;
      const res = await api.getAdminAllStudents({ teacher_id: tid, anno });
      setStudents(res.data);
    } catch { toast.error('Errore nel caricamento alunni'); }
    finally { setLoading(false); }
  }, [teacherId, anno]);

  const handleSwitchStatus = async (student) => {
    if (!anno) { toast.error("Seleziona l'anno accademico nell'header prima di cambiare stato"); return; }
    const currentIscritto = !!student.is_iscritto;
    const target = currentIscritto ? 'prova' : 'iscritto';
    const msg = target === 'prova'
      ? `Segnare ${student.name.toUpperCase()} come PROVA per l'A.A. ${anno}/${String(anno + 1).slice(-2)}?`
      : `Segnare ${student.name.toUpperCase()} come ISCRITTO per l'A.A. ${anno}/${String(anno + 1).slice(-2)}?`;
    if (!window.confirm(msg)) return;
    try {
      const res = await api.setStudentStatus(student.id, target, anno);
      toast.success(`${student.name.toUpperCase()}: ${target.toUpperCase()} (A.A. ${anno}/${String(anno + 1).slice(-2)})`);
      loadStudents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore switch stato');
    }
  };

  useEffect(() => { loadStudents(); }, [loadStudents]);
  useEffect(() => { if (students.length) loadStudentsListinoStatus(students); }, [students, loadStudentsListinoStatus]);
  useEffect(() => { api.getStudentsPaymentSummary(anno).then(res => setPaymentSummary(res.data)).catch(() => {}); }, [anno]);
  useEffect(() => { api.getEmailTemplates().then(res => setEmailTemplates(res.data.filter(t => (t.target_type || 'alunni') === 'alunni'))).catch(() => {}); }, []);

  const getKey = (s) => {
    // Match diretto: student.codice == payment.codice_alunno (numerici dal gestionale).
    if (s.codice) return String(s.codice).trim().toUpperCase();
    // Ultimo fallback: nome normalizzato (per alunni senza codice)
    return (s.name || '')
      .replace(/[\u2019\u2018]/g, "'")
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();
  };

  const addEmail = () => setForm({...form, emails: [...form.emails, { address: '', label: 'Padre' }]});
  const removeEmail = (i) => setForm({...form, emails: form.emails.filter((_, idx) => idx !== i)});
  const updateEmail = (i, field, value) => { const newEmails = [...form.emails]; newEmails[i] = {...newEmails[i], [field]: value}; setForm({...form, emails: newEmails}); };

  const openNew = () => { setEditing(null); setForm({ name: '', phone: '', phone2: '', data_nascita: '', emails: [{ address: '', label: 'Padre' }] }); setShowModal(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, phone: s.phone || '', phone2: s.phone2 || '', data_nascita: s.data_nascita || '', emails: s.emails?.length ? [...s.emails] : [] }); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    const cleanEmails = form.emails.filter(em => em.address.trim());
    try {
      if (editing) {
        await api.updateAdminStudent(editing.id, { name: form.name, phone: form.phone, phone2: form.phone2, data_nascita: form.data_nascita, emails: cleanEmails });
        toast.success('Alunno aggiornato');
        setShowModal(false); loadStudents();
      } else {
        const res = await api.createAdminStudent({ name: form.name, phone: form.phone, phone2: form.phone2, data_nascita: form.data_nascita, emails: cleanEmails }, anno);
        toast.success(`Alunno creato${anno ? ` per l'anno ${anno}` : ''}`);
        setShowModal(false);
        loadStudents();
        // Apri subito la modale per assegnare un orario nel calendario di un insegnante
        const preferredTid = teacherId && teacherId !== 'all' ? teacherId : '';
        openAssignSchedule(res.data, preferredTid);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id) => {
    const msg = anno
      ? `Rimuovere l'alunno dall'anno ${anno}? Verranno cancellate solo le lezioni del ${anno}. Se l'alunno non ha lezioni in altri anni, verrà eliminato completamente.`
      : "Eliminare l'alunno e tutte le sue lezioni di ogni anno?";
    if (!window.confirm(msg)) return;
    try {
      const res = await api.deleteAdminStudent(id, anno);
      toast.success(res.data.message || 'Alunno eliminato');
      loadStudents();
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  const loadStudentPayments = async (studentId, yearFilter) => {
    setPaymentsLoading(true);
    setSelectedPaymentIds([]);
    try {
      const res = await api.getStudentPayments(studentId, yearFilter || undefined);
      const d = res.data;
      // Nuovo formato (oggetto con records/totals/available_anni_competenza).
      // Retrocompatibile con vecchio formato (array puro) se serve.
      if (Array.isArray(d)) {
        setPaymentsData(d);
      } else {
        setPaymentsData(d.records || []);
        if (d.totals) setPaymentsTotals(d.totals);
        if (d.available_anni_competenza) setPaymentsAvailableYears(d.available_anni_competenza);
      }
    } catch { toast.error('Errore caricamento movimenti'); }
    finally { setPaymentsLoading(false); }
  };

  const openPayments = async (s) => {
    setPaymentsStudent(s); setShowPayments(true); setPaymentsYear(anno ? String(anno) : '');
    setSelectedPaymentIds([]);
    // Default: filtro sui docenti effettivi dell'alunno (evita movimenti di
    // altri studenti che condividono lo stesso nome)
    setPaymentsTeacherFilter('own');
    await loadStudentPayments(s.id, anno);
  };

  // Lista dei docenti reali dello studente (dalle sue lezioni)
  const paymentsStudentTeacherIds = useMemo(() => {
    if (!paymentsStudent) return [];
    const ids = new Set();
    (paymentsStudent.lessons || []).forEach(l => {
      if (l.teacher_id) ids.add(String(l.teacher_id));
    });
    return Array.from(ids);
  }, [paymentsStudent]);
  const paymentsStudentTeachers = useMemo(() => {
    if (!paymentsStudent) return [];
    const names = new Set();
    (paymentsStudent.lessons || []).forEach(l => {
      if (l.teacher_name) names.add(l.teacher_name);
    });
    return Array.from(names);
  }, [paymentsStudent]);

  // Filtro pagamenti per insegnante + lista distinct dei nomi insegnante presenti
  const paymentsTeacherList = useMemo(() => {
    const names = new Set();
    paymentsData.forEach(p => { if (p.teacher_name) names.add(p.teacher_name); });
    return Array.from(names).sort();
  }, [paymentsData]);
  // Editor inline del codice alunno (numerico dal gestionale)
  const [editingCodiceId, setEditingCodiceId] = useState(null);
  const [editingCodiceValue, setEditingCodiceValue] = useState('');
  const saveInlineCodice = async (studentId) => {
    const val = String(editingCodiceValue || '').trim();
    try {
      await api.adminUpdateStudent(studentId, { codice: val });
      setStudents(prev => prev.map(x => x.id === studentId ? { ...x, codice: val } : x));
      toast.success('Codice aggiornato');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore aggiornamento codice'); }
    finally { setEditingCodiceId(null); setEditingCodiceValue(''); }
  };
  const paymentsFiltered = useMemo(() => {
    if (paymentsTeacherFilter === 'all') return paymentsData;
    if (paymentsTeacherFilter === 'own') {
      if (paymentsStudentTeacherIds.length === 0) return paymentsData;
      return paymentsData.filter(p => {
        if (paymentsStudentTeacherIds.includes(String(p.teacher_id || ''))) return true;
        const desc = (p.descrizione || '').toLowerCase();
        if (desc.includes('tessera')) return true;
        return false;
      });
    }
    return paymentsData.filter(p => (p.teacher_name || '') === paymentsTeacherFilter);
  }, [paymentsData, paymentsTeacherFilter, paymentsStudentTeacherIds]);

  const togglePaymentSelect = (id) => setSelectedPaymentIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const togglePaymentSelectAll = () => {
    if (selectedPaymentIds.length === paymentsFiltered.length) setSelectedPaymentIds([]);
    else setSelectedPaymentIds(paymentsFiltered.map(p => p.id));
  };
  const selectOnlyScaduti = () => {
    const today = new Date().toISOString().split('T')[0];
    setSelectedPaymentIds(paymentsFiltered.filter(p => !p.data_pagamento && p.data_scadenza && p.data_scadenza < today).map(p => p.id));
  };
  const selectOnlyNonPagati = () => {
    setSelectedPaymentIds(paymentsFiltered.filter(p => !p.data_pagamento).map(p => p.id));
  };

  const downloadEstrattoContoSelected = async () => {
    if (!selectedPaymentIds.length) { toast.error('Seleziona almeno un movimento'); return; }
    if (!paymentsStudent) return;
    setDownloadingSelectivePdf(true);
    try {
      const res = await api.downloadEstrattoContoSelective(paymentsStudent.id, selectedPaymentIds);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `EstrattoConto_${paymentsStudent.name.replace(/ /g, '_')}_${today}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF estratto conto generato');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore generazione PDF');
    } finally {
      setDownloadingSelectivePdf(false);
    }
  };

  const openEmailFromPayments = async () => {
    if (!selectedPaymentIds.length) { toast.error('Seleziona almeno un movimento'); return; }
    if (!paymentsStudent) return;
    // Assicurati che i template siano caricati
    try {
      const res = await api.getEmailTemplates();
      setEmailTemplates(res.data.filter(t => (t.target_type || 'alunni') === 'alunni'));
    } catch { /* usa quelli già caricati */ }
    const s = paymentsStudent;
    const allRecipients = (s.emails || []).filter(em => em.address).map(em => em.address);
    setEmailStudent(s); setEmailMode('template'); setTestMode(false);
    setEmailForm({ subject: '', body: '', template_id: '', selectedRecipients: [...allRecipients] });
    setEmailSelectedPaymentIds([...selectedPaymentIds]);
    setEmailFromPayments(true);
    setEmailStep('compose'); setExtraFiles([]); setShowEmailModal(true);
  };

  const toggleLessonFlag = async (studentId, lessonId, flag, currentActive) => {
    const newActive = !currentActive;
    // Optimistic update
    setStudents(prev => prev.map(s => {
      if (s.id !== studentId) return s;
      return {
        ...s,
        lessons: (s.lessons || []).map(l => {
          if (l.id !== lessonId) return l;
          const set = new Set(l.status_flags || []);
          if (newActive) set.add(flag); else set.delete(flag);
          const ordered = STATUS_FLAGS_ORDER.filter(f => set.has(f));
          return { ...l, status_flags: ordered };
        }),
      };
    }));
    try {
      await api.setLessonStatusFlag(lessonId, flag, newActive);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore aggiornamento flag');
      // Reload to restore original state
      loadStudents();
    }
  };

  const toggleLessonProva = async (studentId, lessonId, currentActive) => {
    const newValue = !currentActive;
    // Ottimistico
    setStudents(prev => prev.map(s => {
      if (s.id !== studentId) return s;
      return {
        ...s,
        lessons: (s.lessons || []).map(l => l.id === lessonId ? { ...l, is_prova: newValue } : l),
      };
    }));
    try {
      await api.setLessonProva(lessonId, newValue);
      toast.success(newValue ? 'Lezione marcata come PROVA' : 'PROVA rimossa dalla lezione');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore aggiornamento PROVA');
      loadStudents();
    }
  };

  // ---- Mail "Conferma Inizio Lezioni" ----
  const [showLessonStartModal, setShowLessonStartModal] = useState(false);
  const [lsSending, setLsSending] = useState(false);
  const [lsPreview, setLsPreview] = useState(null);
  const [lsForm, setLsForm] = useState({
    start_date: '',
    test_email: '',
  });

  const openLessonStartModal = () => {
    // Default start_date: next monday
    const d = new Date();
    const day = d.getDay();
    const diff = (8 - day) % 7 || 7;
    const nextMon = new Date(d);
    nextMon.setDate(d.getDate() + diff);
    const iso = nextMon.toISOString().slice(0, 10);
    setLsForm(prev => ({ ...prev, start_date: iso }));
    setLsPreview(null);
    setShowLessonStartModal(true);
  };

  // Determina l'anno accademico effettivo: usa selectedYear se presente, altrimenti derivato dalla data di inizio.
  // (Es. start_date=2026-09-14 → anno=2026; start_date=2027-01-15 → anno=2026 perché ancora nell'A.A. 2026/27)
  const getLsAnno = () => {
    if (anno) return anno;
    if (!lsForm.start_date) return 0;
    try {
      const [y, m] = lsForm.start_date.split('-').map(Number);
      // Se il mese è >=9 (settembre) l'anno accademico è l'anno del start_date;
      // se il mese è <9 (gen-ago) l'anno accademico inizia nel settembre precedente
      return m >= 9 ? y : y - 1;
    } catch { return 0; }
  };

  const refreshLessonStartPreview = async () => {
    if (!selectedIds.length) return;
    try {
      const res = await api.previewLessonStartEmail({ ...lsForm, student_ids: selectedIds, anno: getLsAnno() });
      setLsPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore anteprima');
    }
  };

  const handleSendLessonStart = async () => {
    if (!selectedIds.length) return;
    if (!lsForm.start_date) { toast.error('Seleziona la data di inizio lezioni'); return; }
    const usedAnno = getLsAnno();
    if (!usedAnno) { toast.error('Impossibile determinare l\'anno accademico. Imposta una data di inizio o seleziona un anno nell\'header.'); return; }
    const isTest = !!lsForm.test_email.trim();
    const msg = isTest
      ? `INVIO DI PROVA (A.A. ${usedAnno}/${String(usedAnno + 1).slice(-2)}): tutte le ${selectedIds.length} mail andranno a "${lsForm.test_email}". Procedere?`
      : `Inviare la mail "Conferma Inizio Lezioni" (A.A. ${usedAnno}/${String(usedAnno + 1).slice(-2)}) a ${selectedIds.length} alunni?`;
    if (!window.confirm(msg)) return;
    setLsSending(true);
    try {
      const res = await api.sendLessonStartEmail({ ...lsForm, student_ids: selectedIds, anno: usedAnno });
      toast.success(`Inviate ${res.data.sent} · Saltate ${res.data.skipped}`);
      setShowLessonStartModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio');
    } finally { setLsSending(false); }
  };

  const openEmail = (s) => {
    setEmailStudent(s); setEmailMode('free'); setTestMode(false);
    const allRecipients = (s.emails || []).filter(em => em.address).map(em => em.address);
    setEmailForm({ subject: '', body: '', template_id: '', selectedRecipients: [...allRecipients] });
    setEmailFromPayments(false); setEmailSelectedPaymentIds([]);
    setEmailStep('compose'); setExtraFiles([]); setShowEmailModal(true);
  };

  const openTestEmail = (s) => {
    setEmailStudent(s); setEmailMode('template'); setTestMode(true);
    setEmailForm({ subject: '', body: '', template_id: '', selectedRecipients: [TEST_EMAIL] });
    setEmailFromPayments(false); setEmailSelectedPaymentIds([]);
    setEmailStep('compose'); setExtraFiles([]); setShowEmailModal(true);
  };

  const openConferma = async (s) => {
    const allRecipients = (s.emails || []).filter(em => em.address).map(em => em.address);
    if (!allRecipients.length) { toast.error('L\'alunno non ha un indirizzo email'); return; }
    try {
      await api.seedConfermaTemplate();
      const res = await api.getEmailTemplates();
      const alunniTemplates = res.data.filter(t => (t.target_type || 'alunni') === 'alunni');
      setEmailTemplates(alunniTemplates);
      const conferma = alunniTemplates.find(t => t.name === 'Conferma Iscrizione - Istruzioni per il pagamento');
      setEmailStudent(s); setEmailMode('template');
      setEmailForm({ subject: '', body: '', template_id: conferma?.id || '', selectedRecipients: [...allRecipients] });
      setEmailFromPayments(false); setEmailSelectedPaymentIds([]);
      setEmailStep('compose'); setExtraFiles([]); setShowEmailModal(true);
    } catch { toast.error('Errore preparazione conferma'); }
  };

  const openConfermaIscrizioneAccademia = async (s) => {
    const allRecipients = (s.emails || []).filter(em => em.address).map(em => em.address);
    if (!allRecipients.length) { toast.error('L\'alunno non ha un indirizzo email'); return; }
    try {
      await api.seedConfermaIscrizioneAccademiaTemplate();
      const res = await api.getEmailTemplates();
      const alunniTemplates = res.data.filter(t => (t.target_type || 'alunni') === 'alunni');
      setEmailTemplates(alunniTemplates);
      const conferma = alunniTemplates.find(t => t.name === 'Conferma Iscrizione Accademia');
      if (!conferma) { toast.error('Modello "Conferma Iscrizione Accademia" non trovato'); return; }
      setEmailStudent(s); setEmailMode('template');
      // Pre-carica subject/body renderizzati con i dati dell'alunno
      let subject = '';
      let body = '';
      try {
        const prev = await api.previewEmail({ student_id: s.id, template_id: conferma.id, target_year: anno || undefined });
        subject = prev.data.subject || '';
        body = prev.data.body || '';
      } catch (err) { /* fallback: modale con campi vuoti, l'utente può cambiare template */ }
      setEmailForm({ subject, body, template_id: conferma.id, selectedRecipients: [...allRecipients] });
      setEmailFromPayments(false); setEmailSelectedPaymentIds([]);
      setEmailStep('compose'); setExtraFiles([]); setShowEmailModal(true);
    } catch { toast.error('Errore preparazione conferma iscrizione'); }
  };

  const toggleRecipient = (addr) => {
    setEmailForm(prev => ({...prev, selectedRecipients: prev.selectedRecipients.includes(addr) ? prev.selectedRecipients.filter(r => r !== addr) : [...prev.selectedRecipients, addr]}));
  };

  // Add new email during send and save to student
  const [newSendEmail, setNewSendEmail] = useState({ address: '', label: 'Padre' });
  const addEmailDuringSend = async () => {
    if (!newSendEmail.address.trim()) return;
    const updatedEmails = [...(emailStudent.emails || []), { address: newSendEmail.address.trim(), label: newSendEmail.label }];
    try {
      await api.updateAdminStudent(emailStudent.id, { emails: updatedEmails });
      setEmailStudent({...emailStudent, emails: updatedEmails});
      setEmailForm(prev => ({...prev, selectedRecipients: [...prev.selectedRecipients, newSendEmail.address.trim()]}));
      setNewSendEmail({ address: '', label: 'Padre' });
      toast.success('Email aggiunta all\'anagrafica');
      loadStudents();
    } catch { toast.error('Errore aggiunta email'); }
  };

  const handleSendEmail = async () => {
    if (!emailForm.selectedRecipients.length) { toast.error('Seleziona almeno un destinatario'); return; }
    setSendingEmail(true);
    try {
      const payload = { student_id: emailStudent.id, recipients: emailForm.selectedRecipients };
      // Always send subject/body custom (eventually edited by user); template_id used only to preserve attachments and confirmation token logic
      payload.subject = emailForm.subject;
      payload.body = emailForm.body;
      if (emailMode === 'template' && emailForm.template_id) payload.template_id = emailForm.template_id;
      // Passa l'anno corrente visualizzato in dashboard: se il template non specifica source/target,
      // la conferma iscrizione verrà creata per l'A.A. attualmente visualizzato dall'admin.
      if (anno) { payload.source_year = anno; payload.target_year = anno + 1; }
      if (extraFiles.length > 0) {
        const extra = [];
        for (const f of extraFiles) {
          const buf = await f.arrayBuffer();
          const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
          extra.push({ filename: f.name, content_b64: b64 });
        }
        payload.extra_attachments = extra;
      }
      if (emailFromPayments && emailSelectedPaymentIds.length > 0) {
        payload.selected_payment_ids = emailSelectedPaymentIds;
      }
      const res = await api.sendEmail(payload);
      toast.success(res.data.message);
      setShowEmailModal(false);
      // Ricarica la lista alunni per aggiornare la data "ultimo invio conferma"
      loadStudents();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore invio email'); }
    finally { setSendingEmail(false); }
  };

  // Load preview when template is selected
  const handleTemplateChange = async (templateId) => {
    setEmailForm(prev => ({ ...prev, template_id: templateId }));
    if (!templateId || !emailStudent) return;
    try {
      const res = await api.previewEmail({ student_id: emailStudent.id, template_id: templateId, source_year: anno, target_year: anno ? anno + 1 : undefined });
      setEmailForm(prev => ({ ...prev, template_id: templateId, subject: res.data.subject, body: res.data.body }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore caricamento anteprima'); }
  };

  // Mappa nomi normalizzati → count per rilevare duplicati
  const nameDuplicatesMap = useMemo(() => {
    const m = {};
    for (const s of students) {
      const k = getKey(s);
      m[k] = (m[k] || 0) + 1;
    }
    return m;
  }, [students]);

  const filtered = students.filter(s => {
    if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (enrolledFilter === 'enrolled' && !s.enrolled_confirmed) return false;
    if (enrolledFilter === 'not-enrolled' && s.enrolled_confirmed) return false;
    if (ageMin !== '' || ageMax !== '') {
      const age = calcAge(s.data_nascita);
      if (age === null) return false;
      if (ageMin !== '' && age < Number(ageMin)) return false;
      if (ageMax !== '' && age > Number(ageMax)) return false;
    }
    return true;
  });
  const enrolledCount = students.filter(s => s.enrolled_confirmed).length;

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAll = () => {
    if (selectedIds.length === filtered.length) setSelectedIds([]);
    else setSelectedIds(filtered.map(s => s.id));
  };
  const selectedStudents = students.filter(s => selectedIds.includes(s.id));

  const handleCopyToNextYear = async () => {
    if (!anno) { toast.error('Seleziona prima un anno nella sidebar'); return; }
    const target = anno + 1;
    if (!window.confirm(`Copiare ${selectedIds.length} alunni dall'anno ${anno} all'anno ${target}? Verranno duplicate le loro lezioni settimanali nel nuovo anno.`)) return;
    try {
      const res = await api.copyStudentsToYear({ student_ids: selectedIds, source_year: anno, target_year: target });
      const { created, skipped, students_copied } = res.data;
      toast.success(`${students_copied} alunni copiati nel ${target}: ${created} lezioni create${skipped ? `, ${skipped} saltate (già esistenti)` : ''}`);
      setSelectedIds([]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore copia'); }
  };

  const openMassEmail = () => { setMassEmailForm({ template_id: '' }); setMassPreview(null); setMassEditedSubject(''); setMassEditedBody(''); setShowMassEmail(true); };
  const handleMassTemplateChange = async (templateId) => {
    setMassEmailForm({ template_id: templateId });
    if (!templateId) { setMassPreview(null); setMassEditedSubject(''); setMassEditedBody(''); return; }
    // Get original template raw subject/body (with placeholders)
    const tpl = emailTemplates.find(t => t.id === templateId);
    if (tpl) {
      setMassEditedSubject(tpl.subject || '');
      setMassEditedBody(tpl.body || '');
      refreshMassPreview(tpl.subject || '', tpl.body || '', templateId);
    }
  };
  const handleSendMassEmail = async () => {
    if (!massEmailForm.template_id) { toast.error('Seleziona un modello'); return; }
    const studentsWithEmail = selectedStudents.filter(s => s.emails?.some(em => em.address));
    if (!studentsWithEmail.length) { toast.error('Nessun alunno con email'); return; }
    setSendingMass(true);
    let sent = 0; let errors = 0;
    for (const s of studentsWithEmail) {
      const recipients = s.emails.filter(em => em.address).map(em => em.address);
      try {
        await api.sendEmail({ student_id: s.id, template_id: massEmailForm.template_id, recipients, subject: massEditedSubject, body: massEditedBody, source_year: anno, target_year: anno ? anno + 1 : undefined });
        sent++;
      } catch { errors++; }
    }
    setSendingMass(false);
    setShowMassEmail(false);
    setSelectedIds([]);
    if (errors === 0) toast.success(`Email inviate a ${sent} alunni`);
    else toast.warning(`Inviate ${sent}, errori ${errors}`);
    // Ricarica per aggiornare le date di ultimo invio
    loadStudents();
  };

  const [dobImporting, setDobImporting] = useState(false);
  const [dobResult, setDobResult] = useState(null);
  const handleImportBirthdates = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setDobImporting(true);
    try {
      const res = await api.importStudentBirthdates(file);
      setDobResult(res.data);
      toast.success(`Date di nascita aggiornate: ${res.data.updated}`);
      loadStudents();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore importazione date di nascita'); }
    finally { setDobImporting(false); }
  };

  const handleExportCodes = async () => {
    try {
      const res = await api.exportStudentCodes();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Codici_Alunni.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Errore nel download'); }
  };

  // ---- Booking invite (link personalizzato) ----
  const openBookingModal = async (student) => {
    setBookingStudent(student);
    setBookingUrl('');
    setBookingLinks([]);
    setShowBookingModal(true);
    setBookingLoading(true);
    try {
      try { await api.seedInvitoPrenotazioneTemplate(); } catch { /* ignore */ }
      const res = await api.createStudentBookingToken(student.id);
      setBookingUrl(res.data.url || '');
      setBookingLinks(res.data.links || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore generazione link');
      setShowBookingModal(false);
    } finally { setBookingLoading(false); }
  };

  const copyUrl = (url) => {
    if (!url) return;
    navigator.clipboard.writeText(url).then(
      () => toast.success('Link copiato negli appunti'),
      () => toast.error('Impossibile copiare il link')
    );
  };

  const sendBookingInvite = async () => {
    if (!bookingStudent) return;
    const hasEmail = (bookingStudent.emails || []).some(em => em.address);
    if (!hasEmail) { toast.error('L\'alunno non ha un indirizzo email'); return; }
    setBookingSending(true);
    try {
      await api.sendStudentBookingInvite(bookingStudent.id);
      toast.success(`Invito inviato a ${bookingStudent.name.toUpperCase()}`);
      setShowBookingModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio invito');
    } finally { setBookingSending(false); }
  };

  const handleBulkBookingInvite = async () => {
    const withEmail = selectedStudents.filter(s => s.emails?.some(em => em.address));
    if (!withEmail.length) { toast.error('Nessun alunno selezionato ha un indirizzo email'); return; }
    if (!window.confirm(`Inviare l'invito di prenotazione a ${withEmail.length} alunno/i selezionato/i?`)) return;
    setBulkBookingSending(true);
    try {
      try { await api.seedInvitoPrenotazioneTemplate(); } catch { /* ignore */ }
      const res = await api.bulkSendBookingInvite(withEmail.map(s => s.id));
      const { sent, errors } = res.data;
      if (errors && errors.length) toast.warning(`Inviati ${sent}, errori ${errors.length}`);
      else toast.success(`Inviti inviati a ${sent} alunni`);
      setSelectedIds([]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore invio inviti'); }
    finally { setBulkBookingSending(false); }
  };

  return (
    <>
      <div className="mb-6 animate-fade-in flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div><h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight mb-2">Lista Alunni</h1><p className="text-[#52525B] text-sm">Gestisci gli alunni della scuola</p></div>
        <Button onClick={openNew} className="bg-[#0A0A0A] rounded-sm" data-testid="admin-add-student-btn"><Plus size={18} className="mr-2" />Nuovo Alunno</Button>
      </div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4 flex-wrap animate-fade-in animate-delay-1">
        <Select value={teacherId} onValueChange={setTeacherId}>
          <SelectTrigger className="w-full sm:w-[260px] rounded-sm h-9 text-sm bg-[#0A0A0A] text-white border-[#0A0A0A] font-medium [&>svg]:text-white" data-testid="admin-students-teacher-filter"><SelectValue placeholder="Tutti gli insegnanti" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli insegnanti</SelectItem>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name?.toUpperCase()} - {t.instrument}</SelectItem>)}</SelectContent>
        </Select>
        <div className="relative w-full sm:w-auto"><MagnifyingGlass size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A1A1AA]" /><Input type="text" placeholder="Cerca alunno..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full sm:w-[200px] rounded-sm h-9 text-sm pl-8 uppercase" data-testid="admin-students-search" /></div>
        <div className="inline-flex items-center gap-1.5 bg-white border border-[#E4E4E7] rounded-sm h-9 px-2 text-xs text-[#52525B]" data-testid="age-filter">
          <span className="font-medium">Età</span>
          <input type="number" min="0" max="99" value={ageMin} onChange={(e) => setAgeMin(e.target.value)} placeholder="da" className="w-12 h-7 border border-[#E4E4E7] rounded-sm px-1 text-center text-sm" data-testid="age-filter-min" />
          <span>–</span>
          <input type="number" min="0" max="99" value={ageMax} onChange={(e) => setAgeMax(e.target.value)} placeholder="a" className="w-12 h-7 border border-[#E4E4E7] rounded-sm px-1 text-center text-sm" data-testid="age-filter-max" />
          {(ageMin !== '' || ageMax !== '') && <button type="button" onClick={() => { setAgeMin(''); setAgeMax(''); }} className="text-[#A1A1AA] hover:text-red-600" title="Azzera filtro età" data-testid="age-filter-clear"><X size={14} weight="bold" /></button>}
        </div>
        <div className="inline-flex bg-white border border-[#E4E4E7] rounded-sm p-0.5" data-testid="enrolled-filter">
          <button onClick={() => setEnrolledFilter('all')} className={`px-3 h-8 text-xs font-medium rounded-sm transition-colors ${enrolledFilter === 'all' ? 'bg-[#2D2A4A] text-white' : 'text-[#52525B] hover:bg-[#F4F4F5]'}`} data-testid="enrolled-filter-all">Tutti</button>
          <button onClick={() => setEnrolledFilter('enrolled')} className={`px-3 h-8 text-xs font-medium rounded-sm transition-colors inline-flex items-center gap-1 ${enrolledFilter === 'enrolled' ? 'bg-green-600 text-white' : 'text-green-700 hover:bg-green-50'}`} data-testid="enrolled-filter-yes">
            <CheckCircle size={14} weight={enrolledFilter === 'enrolled' ? 'fill' : 'regular'} />Iscritti ({enrolledCount})
          </button>
          <button onClick={() => setEnrolledFilter('not-enrolled')} className={`px-3 h-8 text-xs font-medium rounded-sm transition-colors ${enrolledFilter === 'not-enrolled' ? 'bg-amber-500 text-white' : 'text-amber-700 hover:bg-amber-50'}`} data-testid="enrolled-filter-no">Da iscrivere</button>
        </div>
        <Button onClick={handleExportCodes} variant="outline" size="sm" className="rounded-sm h-9 text-sm" data-testid="export-student-codes-btn"><DownloadSimple size={16} className="mr-1" />Esporta Codici</Button>
        <label className={`inline-flex items-center h-9 px-3 text-sm font-medium border border-[#E4E4E7] bg-white rounded-sm cursor-pointer hover:bg-[#F4F4F5] transition-colors ${dobImporting ? 'opacity-50 pointer-events-none' : ''}`} data-testid="import-birthdates-btn">
          <FileText size={16} className="mr-1" />{dobImporting ? 'Importazione...' : 'Importa Date di Nascita (CSV)'}
          <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleImportBirthdates} data-testid="import-birthdates-input" />
        </label>
        {selectedIds.length > 0 && (
          <>
            <Button onClick={openMassEmail} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm h-9 text-sm" data-testid="mass-email-btn">
              <EnvelopeSimple size={16} className="mr-1" />Invia Email ({selectedIds.length})
            </Button>
            <Button onClick={() => openLessonStartModal()} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm h-9 text-sm" data-testid="lesson-start-email-btn">
              <PaperPlaneTilt size={16} className="mr-1" />Conferma Inizio Lezioni ({selectedIds.length})
            </Button>
            <Button onClick={handleBulkBookingInvite} disabled={bulkBookingSending} className="bg-[#F0D060] hover:bg-[#FFE580] text-[#2D2A4A] rounded-sm h-9 text-sm font-semibold" data-testid="bulk-booking-invite-btn">
              <LinkIcon size={16} className="mr-1" />{bulkBookingSending ? 'Invio...' : `Invia Link Prenotazione (${selectedIds.length})`}
            </Button>
            <Button onClick={handleCopyToNextYear} className="bg-amber-600 hover:bg-amber-700 rounded-sm h-9 text-sm" data-testid="copy-students-next-year-btn" disabled={!anno}>
              <CheckSquare size={16} className="mr-1" />Copia a {anno ? anno + 1 : '?'} ({selectedIds.length})
            </Button>
          </>
        )}
        <div className="bg-white border border-[#E4E4E7] rounded-sm px-4 py-1.5 text-sm ml-auto" data-testid="admin-students-total"><span className="text-[#52525B]">Totale:</span> <span className="font-bold text-lg font-['Work_Sans']">{filtered.length}</span><span className="text-[#52525B] ml-1">alunni</span></div>
      </div>
      <div className="animate-fade-in animate-delay-2 overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
        {loading ? <div className="text-center py-12 text-[#52525B]">Caricamento...</div> : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white border border-[#E4E4E7] rounded-sm"><Users size={48} className="mx-auto text-[#E4E4E7] mb-4" /><p className="text-[#52525B]">Nessun alunno trovato</p></div>
        ) : (
          <table className="data-table students-compact-table" data-testid="admin-students-table">
            <thead><tr><th className="w-8"><Checkbox checked={selectedIds.length > 0 && selectedIds.length === filtered.length} onCheckedChange={toggleAll} data-testid="select-all-students" /></th><th>#</th><th>Codice</th><th>Alunno</th><th>Lezioni</th><th>Telefono</th><th>Email</th><th className="text-right">Tot</th><th className="text-right">Pag</th><th className="text-right">Da Pag</th><th className="text-right">Scad</th><th className="text-right">Azioni</th></tr></thead>
            <tbody>{filtered.map((s, i) => {
              const ps = paymentSummary[getKey(s)] || { total: 0, paid: 0, due: 0, overdue: 0 };
              const hasEmail = s.emails?.some(em => em.address);
              const isDuplicate = (nameDuplicatesMap[getKey(s)] || 0) > 1;
              return (<tr key={s.id} className={selectedIds.includes(s.id) ? 'bg-blue-50/50' : (isDuplicate ? 'bg-red-50/60' : (s.enrolled_confirmed ? 'bg-green-50/30' : ''))}>
                <td className="w-10"><Checkbox checked={selectedIds.includes(s.id)} onCheckedChange={() => toggleSelect(s.id)} data-testid={`select-student-${s.id}`} /></td>
                <td className="text-sm text-[#A1A1AA] w-12">{i + 1}</td>
                <td className="text-xs font-mono text-[#2D2A4A] font-semibold">
                  {editingCodiceId === s.id ? (
                    <input
                      type="text"
                      value={editingCodiceValue}
                      onChange={(e) => setEditingCodiceValue(e.target.value)}
                      onBlur={() => saveInlineCodice(s.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setEditingCodiceId(null); setEditingCodiceValue(''); } }}
                      autoFocus
                      className="w-20 border border-[#2D2A4A] rounded px-1 py-0.5 font-mono text-xs bg-yellow-50"
                      data-testid={`edit-codice-input-${s.id}`}
                    />
                  ) : (
                    <span
                      onClick={() => { setEditingCodiceId(s.id); setEditingCodiceValue(s.codice && !String(s.codice).toUpperCase().startsWith('ALU') ? s.codice : ''); }}
                      className="cursor-pointer hover:bg-yellow-50 hover:border hover:border-yellow-400 border border-transparent rounded px-1 py-0.5 inline-block min-w-[40px]"
                      title="Clicca per modificare il codice"
                      data-testid={`edit-codice-btn-${s.id}`}
                    >
                      {s.codice && !String(s.codice).toUpperCase().startsWith('ALU') ? s.codice : '-'}
                    </span>
                  )}
                </td>
                <td className="text-sm font-medium uppercase">
                  {s.name}
                  {s.data_nascita && (
                    <span className="ml-2 text-[10px] font-normal text-[#71717A] normal-case align-middle whitespace-nowrap" title="Data di nascita" data-testid={`student-dob-${s.id}`}>{s.data_nascita.split('-').reverse().join('/')}{calcAge(s.data_nascita) !== null && <b className="ml-1 text-[#2D2A4A]" data-testid={`student-age-${s.id}`}>· {calcAge(s.data_nascita)} anni</b>}</span>
                  )}
                  {isDuplicate && (
                    <span className="ml-2 inline-flex items-center text-[9px] font-bold text-red-700 bg-red-100 border border-red-400 px-1.5 py-0.5 rounded-full uppercase align-middle" title="Nome duplicato: esistono più alunni con questo stesso nome. Cancella quelli in eccesso." data-testid={`dup-badge-${s.id}`}>⚠ DUPLICATO</span>
                  )}
                  {s.created_from_booking && (
                    <span className="ml-2 inline-flex items-center text-[9px] font-bold text-purple-700 bg-purple-100 border border-purple-300 px-1.5 py-0.5 rounded-full uppercase align-middle" title="Alunno creato da una prenotazione accettata" data-testid={`booking-new-badge-${s.id}`}>Nuovo</span>
                  )}
                  {(s.is_iscritto || s.is_prova_state) && (
                    <button
                      type="button"
                      onClick={() => handleSwitchStatus(s)}
                      className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider rounded align-middle transition-colors cursor-pointer ${s.is_iscritto ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'}`}
                      title={s.is_iscritto ? `ISCRITTO — Click per riportare a PROVA (A.A. ${anno || '?'})` : `PROVA — Click per segnare ISCRITTO (A.A. ${anno || '?'})`}
                      data-testid={`student-status-badge-${s.id}`}
                    >
                      {s.is_iscritto ? 'ISCRITTO' : 'PROVA'}
                    </button>
                  )}
                  {(s.lessons?.length || 0) > 0 && (() => {
                    // Un badge per ciascun insegnante con il NUMERO DI PRESENZE GENERATE
                    // per quella coppia alunno/insegnante (NON gli slot settimanali).
                    // Serve a capire se le presenze per quell'alunno con quel docente sono già state generate.
                    const teacherNameMap = new Map();
                    for (const l of s.lessons) {
                      const tid = l.teacher_id || '';
                      const tname = l.teacher_name || '—';
                      if (!teacherNameMap.has(tid)) teacherNameMap.set(tid, tname);
                    }
                    const attByTeacher = s.attendance_count_by_teacher || {};
                    const entries = Array.from(teacherNameMap.entries())
                      .map(([tid, tname]) => ({ tid, tname, count: attByTeacher[tid] || 0 }))
                      .sort((a, b) => a.tname.localeCompare(b.tname));
                    const lastName = (full) => (full || '').trim().split(/\s+/)[0] || '';
                    return entries.map((e, idx) => {
                      const generated = e.count > 0;
                      const cls = generated
                        ? 'bg-indigo-100 text-indigo-800 border-indigo-300'
                        : 'bg-red-100 text-red-800 border-red-300';
                      return (
                        <span
                          key={idx}
                          className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider rounded align-middle border ${cls}`}
                          title={generated
                            ? `${e.tname}: ${e.count} presenze generate`
                            : `${e.tname}: nessuna presenza generata — clicca "Genera Presenze" sulla riga`}
                          data-testid={`student-lessons-count-badge-${s.id}-${idx}`}
                        >
                          <ClipboardText size={10} weight="bold" />
                          {lastName(e.tname)} {e.count}
                        </span>
                      );
                    });
                  })()}
                </td>
                <td data-testid={`lessons-cell-${s.id}`}>
                  {s.lessons?.length ? (
                    <div className="space-y-1 py-0.5">
                      {s.lessons.map(l => {
                        const active = new Set(l.status_flags || []);
                        return (
                          <div key={l.id} className="flex items-center gap-2 text-[10px]" data-testid={`student-lesson-${l.id}`}>
                            <div className="flex flex-col min-w-[130px] leading-tight">
                              <span className="font-semibold text-[10px] uppercase whitespace-nowrap">{l.teacher_name || '—'}</span>
                              <span className="text-[10px] text-[#52525B] whitespace-nowrap">{DAY_NAMES[l.day] || '?'} {l.start_time}-{l.end_time}</span>
                            </div>
                            <div className="flex gap-0.5">
                              {STATUS_FLAGS_ORDER.map(f => {
                                const isOn = active.has(f);
                                return (
                                  <button
                                    key={f}
                                    type="button"
                                    onClick={() => toggleLessonFlag(s.id, l.id, f, isOn)}
                                    className={`h-5 min-w-[22px] px-1 text-[9px] font-extrabold uppercase tracking-tighter rounded border transition-colors ${isOn ? 'bg-green-500 text-white border-green-600' : 'bg-white text-[#71717A] border-[#D4D4D8] hover:bg-[#F4F4F5]'}`}
                                    title={`${STATUS_FLAG_LABELS[f]}: ${isOn ? 'attivo' : 'non attivo'}`}
                                    data-testid={`student-flag-btn-${l.id}-${f}`}
                                  >
                                    {STATUS_FLAG_LABELS[f]}
                                  </button>
                                );
                              })}
                              {l.is_prova && (
                                <span
                                  className="h-5 px-1.5 text-[9px] font-extrabold uppercase tracking-tighter rounded ml-1 bg-amber-500 text-white border border-amber-600 flex items-center"
                                  title="Lezione contrassegnata come PROVA (usa il badge PROVA/ISCRITTO vicino al nome per cambiare stato)"
                                >
                                  P
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : <span className="text-[#D4D4D8] text-xs">—</span>}
                </td>
                <td className="text-sm text-[#52525B]">{(s.phone || s.phone2) ? (
                  <div className="space-y-0.5">
                    {[s.phone, s.phone2].filter(Boolean).map((ph, i) => {
                      const normalized = ph.replace(/[^\d+]/g, '').replace(/^\+/, '').replace(/^00/, '');
                      // se non inizia con prefisso paese (>10 cifre), assumiamo Italia (+39)
                      const withCc = normalized.length > 10 ? normalized : `39${normalized}`;
                      return (
                        <div key={i} className="flex items-center gap-1.5 text-xs">
                          <span>{ph}</span>
                          <a
                            href={`https://wa.me/${withCc}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white transition-colors"
                            title={`Apri WhatsApp per ${ph}`}
                            data-testid={`whatsapp-btn-${s.id}-${i}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <WhatsappLogo size={12} weight="bold" />
                          </a>
                        </div>
                      );
                    })}
                  </div>
                ) : <span className="text-[#D4D4D8]">-</span>}</td>
                <td className="text-sm text-[#52525B]">{s.emails?.length > 0 ? (
                  <div className="space-y-0.5">{s.emails.map((em, idx) => em.address ? <div key={idx} className="text-xs"><span className="text-[#A1A1AA]">{em.label}:</span> {em.address}</div> : null)}</div>
                ) : <span className="text-[#D4D4D8]">-</span>}</td>
                <td className="text-sm text-right font-['Work_Sans']">{ps.total ? parseFloat(ps.total).toFixed(2) : <span className="text-[#D4D4D8]">-</span>}</td>
                <td className="text-sm text-right font-['Work_Sans'] text-green-600">{ps.paid ? parseFloat(ps.paid).toFixed(2) : <span className="text-[#D4D4D8]">-</span>}</td>
                <td className="text-sm text-right font-['Work_Sans'] text-amber-600">{ps.due ? parseFloat(ps.due).toFixed(2) : <span className="text-[#D4D4D8]">-</span>}</td>
                <td className="text-sm text-right font-['Work_Sans'] text-red-600 font-semibold">{ps.overdue ? parseFloat(ps.overdue).toFixed(2) : <span className="text-[#D4D4D8]">-</span>}</td>
                <td className="text-right whitespace-nowrap"><div className="flex items-center justify-end gap-1">
                  {ps.total > 0 && <button onClick={() => openPayments(s)} className="p-1.5 rounded bg-amber-700 hover:bg-amber-800 text-white border border-amber-800 transition" title="Dettaglio pagamenti" data-testid={`admin-payments-detail-${s.id}`}><CurrencyEur size={15} weight="bold" /></button>}
                  <button onClick={() => openBookingModal(s)} className="p-1.5 rounded bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border border-yellow-300 transition" title="Genera/invia link prenotazione personalizzato" data-testid={`admin-booking-invite-${s.id}`}><LinkIcon size={15} weight="bold" /></button>
                  {hasEmail && <button onClick={() => openEmail(s)} className="p-1.5 rounded bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-300 transition" title="Invia email" data-testid={`admin-send-email-${s.id}`}><EnvelopeSimple size={15} weight="bold" /></button>}
                  {hasEmail && (() => {
                    const lastSent = s.conferma_iscrizione_last_sent_at;
                    let tooltip = 'Invia Conferma Iscrizione Accademia';
                    if (lastSent) {
                      try {
                        const d = new Date(lastSent);
                        tooltip = `Ultimo invio: ${d.toLocaleDateString('it-IT')} ${d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
                      } catch { /* ignore */ }
                    }
                    return (
                      <button onClick={() => openConfermaIscrizioneAccademia(s)} className={`p-1.5 rounded border transition ${lastSent ? 'bg-[#2D2A4A] hover:bg-[#1e1c38] text-white border-[#2D2A4A]' : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-300'}`} title={tooltip} data-testid={`admin-send-conferma-iscrizione-${s.id}`}>
                        <CheckCircle size={15} weight="bold" />
                      </button>
                    );
                  })()}
                  {hasEmail && (studentsListinoMap[s.id]
                    ? <button onClick={() => openTariffe(s)} className="p-1.5 rounded bg-green-700 hover:bg-green-800 text-white border border-green-800 transition" title="Tariffe impostate — clicca per modificare" data-testid={`admin-tariffe-${s.id}`}><Tag size={15} weight="fill" /></button>
                    : <button onClick={() => openTariffe(s)} className="p-1.5 rounded bg-orange-100 hover:bg-orange-200 text-orange-800 border border-orange-500 transition animate-pulse" title="Imposta le tariffe per ogni lezione" data-testid={`admin-tariffe-${s.id}`}><Tag size={15} weight="bold" /></button>
                  )}
                  <button onClick={() => openAssignSchedule(s)} className="p-1.5 rounded bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-300 transition" title="Assegna orario nel calendario di un insegnante" data-testid={`admin-assign-schedule-${s.id}`}><CalendarPlus size={15} weight="bold" /></button>
                  {(s.lessons?.length > 0) && (() => {
                    const totLessons = s.lessons.length;
                    const withAtt = s.lessons_with_attendance || 0;
                    const attCount = s.attendance_count || 0;
                    const allDone = withAtt >= totLessons;
                    const someDone = withAtt > 0;
                    return (
                      <button
                        onClick={() => openGenAttModal(s)}
                        className={`relative p-1.5 rounded border transition ${allDone ? 'bg-teal-700 hover:bg-teal-800 text-white border-teal-800' : someDone ? 'bg-teal-50 hover:bg-teal-100 text-teal-700 border-teal-300' : 'bg-rose-50 hover:bg-rose-100 text-rose-700 border-rose-300'}`}
                        title={attCount > 0 ? `Genera presenze — ${withAtt}/${totLessons} lezioni collegate · ${attCount} record di presenza` : 'Genera presenze per le lezioni dell\'alunno'}
                        data-testid={`admin-generate-attendance-${s.id}`}
                      >
                        <ClipboardText size={15} weight="bold" />
                        {attCount > 0 && (
                          <span className={`absolute -top-1.5 -right-1.5 text-[9px] font-extrabold px-1 py-0 rounded-full leading-tight border ${allDone ? 'bg-white text-teal-800 border-teal-800' : 'bg-teal-600 text-white border-teal-700'}`} data-testid={`attendance-badge-${s.id}`}>
                            {withAtt}/{totLessons}
                          </span>
                        )}
                      </button>
                    );
                  })()}
                  <button onClick={() => openEdit(s)} className="p-1.5 rounded bg-[#F4F4F5] hover:bg-[#E4E4E7] text-[#3F3F46] border border-[#D4D4D8] transition" title="Modifica alunno" data-testid={`admin-edit-student-${s.id}`}><PencilSimple size={15} weight="bold" /></button>
                  <button onClick={() => handleDelete(s.id)} className="p-1.5 rounded bg-red-50 hover:bg-red-100 text-red-700 border border-red-300 transition" title="Elimina alunno" data-testid={`admin-delete-student-${s.id}`}><Trash size={15} weight="bold" /></button>
                </div></td>
              </tr>);
            })}</tbody>
          </table>
        )}
      </div>

      {/* Student CRUD Modal */}
      <Dialog open={!!dobResult} onOpenChange={(o) => { if (!o) setDobResult(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="birthdates-result-dialog">
          <DialogHeader><DialogTitle className="font-['Work_Sans']">Importazione Date di Nascita</DialogTitle><DialogDescription>Righe nel file: {dobResult?.total_rows} · Alunni aggiornati: <b data-testid="birthdates-updated-count">{dobResult?.updated}</b></DialogDescription></DialogHeader>
          {dobResult && (
            <div className="space-y-4 text-sm">
              <div><div className="label-overline mb-1 text-green-700">Corrispondenze esatte ({dobResult.exact.length})</div>
                <div className="text-xs text-[#52525B] uppercase" data-testid="birthdates-exact-list">{dobResult.exact.map(x => `${x.alunno} (${x.data_nascita.split('-').reverse().join('/')})`).join(' · ') || '-'}</div></div>
              <div><div className="label-overline mb-1 text-amber-700">Corrispondenze probabili ({dobResult.probable.length})</div>
                <div className="space-y-0.5" data-testid="birthdates-probable-list">{dobResult.probable.map((x, i) => <div key={i} className="text-xs uppercase">{x.alunno} → <span className="text-[#52525B]">{x.nome_file}</span> ({x.data_nascita.split('-').reverse().join('/')})</div>)}{!dobResult.probable.length && '-'}</div></div>
              <div><div className="label-overline mb-1 text-red-700">Non trovati nel file ({dobResult.not_found.length})</div>
                <div className="text-xs text-[#52525B] uppercase" data-testid="birthdates-notfound-list">{dobResult.not_found.join(' · ') || '-'}</div>
                <p className="text-[11px] text-[#71717A] mt-1">Per questi alunni puoi inserire la data a mano da "Modifica Alunno".</p></div>
            </div>
          )}
          <DialogFooter><Button onClick={() => setDobResult(null)} className="bg-[#0A0A0A] rounded-sm" data-testid="birthdates-result-close">Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Work_Sans']">{editing ? 'Modifica Alunno' : 'Nuovo Alunno'}</DialogTitle><DialogDescription>{editing ? "Modifica i dati dell'alunno" : 'Inserisci i dati del nuovo alunno'}</DialogDescription></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2"><Label className="label-overline">Nome Alunno</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required placeholder="Nome e Cognome" className="rounded-sm uppercase" data-testid="admin-student-name-input" /></div>
            <div className="space-y-2"><Label className="label-overline">Data di nascita</Label><Input type="date" value={form.data_nascita} onChange={(e) => setForm({...form, data_nascita: e.target.value})} className="rounded-sm" data-testid="admin-student-dob-input" /></div>
            <div className="space-y-2"><Label className="label-overline">Telefono 1</Label><Input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="Es. 333 1234567" className="rounded-sm" data-testid="admin-student-phone-input" /></div>
            <div className="space-y-2"><Label className="label-overline">Telefono 2</Label><Input type="tel" value={form.phone2} onChange={(e) => setForm({...form, phone2: e.target.value})} placeholder="Es. 333 7654321" className="rounded-sm" data-testid="admin-student-phone2-input" /></div>
            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label className="label-overline">Email</Label><button type="button" onClick={addEmail} className="text-xs text-[#2D2A4A] font-medium hover:underline" data-testid="add-email-btn">+ Aggiungi email</button></div>
              {form.emails.map((em, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Select value={em.label} onValueChange={(v) => updateEmail(i, 'label', v)}>
                    <SelectTrigger className="w-[120px] rounded-sm h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{EMAIL_LABELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="email" value={em.address} onChange={(e) => updateEmail(i, 'address', e.target.value)} placeholder="nome@email.it" className="rounded-sm h-9 text-sm flex-1" data-testid={`student-email-${i}`} />
                  <button type="button" onClick={() => removeEmail(i)} className="p-1 text-red-500 hover:bg-red-50 rounded"><X size={16} /></button>
                </div>
              ))}
              {form.emails.length === 0 && <p className="text-xs text-[#A1A1AA]">Nessuna email. Clicca &quot;+ Aggiungi email&quot; per aggiungerne.</p>}
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-sm">Annulla</Button><Button type="submit" className="bg-[#0A0A0A] rounded-sm" data-testid="admin-save-student-btn">{editing ? 'Aggiorna' : 'Crea'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Student Payments Modal */}
      <Dialog open={showPayments} onOpenChange={setShowPayments}>
        <DialogContent className="sm:max-w-6xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Work_Sans']">Addebiti - {paymentsStudent?.name}</DialogTitle></DialogHeader>
          {/* Filtro Anno Competenza */}
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <Label className="label-overline text-xs">Anno Competenza</Label>
            <Select value={paymentsYear || 'all'} onValueChange={(v) => { const nv = v === 'all' ? '' : v; setPaymentsYear(nv); if (paymentsStudent) loadStudentPayments(paymentsStudent.id, nv ? parseInt(nv) : undefined); }}>
              <SelectTrigger className="w-[180px] rounded-sm h-9 text-sm" data-testid="student-payments-year"><SelectValue placeholder="Tutti gli anni" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti gli anni</SelectItem>{paymentsAvailableYears.map(a => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
            </Select>
            {(paymentsTeacherList.length > 1 || paymentsStudentTeachers.length > 0) && (
              <>
                <Label className="label-overline text-xs">Insegnante</Label>
                <Select value={paymentsTeacherFilter} onValueChange={setPaymentsTeacherFilter}>
                  <SelectTrigger className="w-[260px] rounded-sm h-9 text-sm" data-testid="student-payments-teacher-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {paymentsStudentTeachers.length > 0 && (
                      <SelectItem value="own">Suoi insegnanti ({paymentsStudentTeachers.join(', ')})</SelectItem>
                    )}
                    <SelectItem value="all">Tutti gli insegnanti</SelectItem>
                    {paymentsTeacherList.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </>
            )}
            <div className="text-xs text-[#52525B] ml-auto">
              {selectedPaymentIds.length > 0 ? (
                <span className="font-semibold text-[#4338CA]">{selectedPaymentIds.length} di {paymentsFiltered.length} selezionati</span>
              ) : (
                <>{paymentsFiltered.length} {paymentsFiltered.length === 1 ? 'addebito' : 'addebiti'}</>
              )}
            </div>
          </div>
          {/* Toolbar Selezione + Azioni */}
          {paymentsFiltered.length > 0 && !paymentsLoading && (
            <div className="flex items-center gap-2 mb-3 flex-wrap bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm p-2">
              <span className="text-[10px] uppercase font-semibold tracking-wider text-[#52525B]">Selezione rapida:</span>
              <Button type="button" size="sm" variant="outline" onClick={selectOnlyScaduti} className="rounded-sm h-7 text-xs" data-testid="select-only-scaduti-btn">Solo Scaduti</Button>
              <Button type="button" size="sm" variant="outline" onClick={selectOnlyNonPagati} className="rounded-sm h-7 text-xs" data-testid="select-only-nonpagati-btn">Solo Non Pagati</Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setSelectedPaymentIds([])} className="rounded-sm h-7 text-xs" data-testid="clear-selection-btn" disabled={selectedPaymentIds.length === 0}>Deseleziona</Button>
              <div className="ml-auto flex items-center gap-2">
                <Button type="button" size="sm" onClick={downloadEstrattoContoSelected} disabled={selectedPaymentIds.length === 0 || downloadingSelectivePdf} className="rounded-sm h-8 bg-[#0A0A0A] text-white text-xs" data-testid="download-selective-pdf-btn">
                  <DownloadSimple size={14} className="mr-1" />{downloadingSelectivePdf ? 'Generazione...' : 'Scarica PDF'}
                </Button>
                <Button type="button" size="sm" onClick={openEmailFromPayments} disabled={selectedPaymentIds.length === 0} className="rounded-sm h-8 bg-[#4338CA] text-white text-xs hover:bg-[#3730A3]" data-testid="send-email-selected-btn">
                  <PaperPlaneTilt size={14} className="mr-1" />Invia Mail
                </Button>
              </div>
            </div>
          )}
          {paymentsLoading ? <div className="text-center py-8 text-[#52525B]">Caricamento...</div> : paymentsFiltered.length === 0 ? <div className="text-center py-8 text-[#52525B]">Nessun addebito trovato</div> : (<>
            {/* Totali orientati all'alunno */}
            {(() => {
              const today = new Date().toISOString().split('T')[0];
              let dovuto = 0, pagato = 0, scaduto = 0;
              paymentsFiltered.forEach(p => {
                const imp = parseFloat(p.importo) || 0;
                dovuto += imp;
                const hasPag = !!p.data_pagamento;
                if (hasPag) pagato += imp;
                else if (p.data_scadenza && p.data_scadenza < today) scaduto += imp;
              });
              return (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
                  <div className="bg-[#EEF2FF] border border-[#C7D2FE] rounded-sm p-2.5" data-testid="student-total-dovuto"><div className="text-[9px] uppercase font-semibold text-[#4338CA]">Totale Dovuto Alunno</div><div className="text-sm font-bold font-['Work_Sans'] text-[#4338CA]">{formatCurrency(dovuto)}</div></div>
                  <div className="bg-green-50 border border-green-200 rounded-sm p-2.5" data-testid="student-total-pagato"><div className="text-[9px] uppercase font-semibold text-green-700">Totale Pagato Alunno</div><div className="text-sm font-bold font-['Work_Sans'] text-green-700">{formatCurrency(pagato)}</div></div>
                  <div className="bg-red-50 border border-red-200 rounded-sm p-2.5" data-testid="student-total-scaduto"><div className="text-[9px] uppercase font-semibold text-red-700">Totale Scaduto Alunno</div><div className="text-sm font-bold font-['Work_Sans'] text-red-700">{formatCurrency(scaduto)}</div></div>
                </div>
              );
            })()}
            <div className="overflow-x-auto border border-[#2D2A4A] rounded-sm">
              <table className="w-full text-xs" data-testid="student-payments-detail-table" style={{borderCollapse: 'collapse'}}>
                <thead><tr className="bg-[#2D2A4A] text-white">
                  <th className="text-center px-2 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={paymentsFiltered.length > 0 && selectedPaymentIds.length === paymentsFiltered.length}
                      ref={el => { if (el) el.indeterminate = selectedPaymentIds.length > 0 && selectedPaymentIds.length < paymentsFiltered.length; }}
                      onChange={togglePaymentSelectAll}
                      className="rounded"
                      data-testid="select-all-payments-checkbox"
                      title="Seleziona / Deseleziona tutti"
                    />
                  </th>
                  <th className="text-left px-2 py-2 font-bold uppercase tracking-wider">Cod. Ins.</th>
                  <th className="text-left px-2 py-2 font-bold uppercase tracking-wider">Insegnante</th>
                  <th className="text-right px-2 py-2 font-bold uppercase tracking-wider">Importo</th>
                  <th className="text-center px-2 py-2 font-bold uppercase tracking-wider">Anno</th>
                  <th className="text-center px-2 py-2 font-bold uppercase tracking-wider">Scadenza</th>
                  <th className="text-left px-2 py-2 font-bold uppercase tracking-wider">Descrizione</th>
                  <th className="text-center px-2 py-2 font-bold uppercase tracking-wider">Pagamento</th>
                  <th className="text-right px-2 py-2 font-bold uppercase tracking-wider">Dovuto Ins.</th>
                  <th className="text-right px-2 py-2 font-bold uppercase tracking-wider">Pagato Ins.</th>
                </tr></thead>
                <tbody>{paymentsFiltered.map(p => {
                  const editable = String(p.id_causale || '') !== '1' && !!p.data_pagamento;
                  const isSelected = selectedPaymentIds.includes(p.id);
                  const isTessera = (p.descrizione || '').toLowerCase().includes('tessera');
                  return (
                  <tr key={p.id} className={`border-b border-[#E4E4E7] hover:bg-yellow-50 ${isSelected ? 'bg-[#EEF2FF]' : ''} ${isTessera && !isSelected ? 'bg-blue-50/40' : ''}`}>
                    <td className="px-2 py-1.5 text-center border-r border-[#E4E4E7]">
                      <input type="checkbox" checked={isSelected} onChange={() => togglePaymentSelect(p.id)} className="rounded" data-testid={`select-payment-${p.id}`} />
                    </td>
                    <td className="px-2 py-1.5 font-mono border-r border-[#E4E4E7]">{p.codice_insegnante || '-'}</td>
                    <td className="px-2 py-1.5 border-r border-[#E4E4E7]">{p.teacher_name || '-'}</td>
                    <td className="px-2 py-1.5 text-right font-medium font-['Work_Sans'] border-r border-[#E4E4E7]">{formatCurrency(parseFloat(p.importo) || 0)}</td>
                    <td className="px-2 py-1.5 text-center font-['Work_Sans'] border-r border-[#E4E4E7]">{p.anno_competenza || '-'}</td>
                    <td className="px-2 py-1.5 text-[#52525B] text-center border-r border-[#E4E4E7]">{formatDate(p.data_scadenza)}</td>
                    <td className="px-2 py-1.5 text-[#52525B] max-w-[180px] truncate" title={p.descrizione}>{p.descrizione || '-'}</td>
                    <td className="px-2 py-1.5 text-[#52525B]">{formatDate(p.data_pagamento)}</td>
                    <td className="px-2 py-1.5 text-right font-['Work_Sans']">{editable && p.importo_insegnante ? Number(p.importo_insegnante).toFixed(2) : '-'}</td>
                    <td className="px-2 py-1.5 text-right font-['Work_Sans']">{editable && p.pagato_insegnante ? Number(p.pagato_insegnante).toFixed(2) : '-'}</td>
                  </tr>);
                })}</tbody>
              </table>
            </div>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Send Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Work_Sans']">{emailStep === 'preview' ? 'Anteprima Email' : `${testMode ? '🧪 INVIO DI PROVA - ' : 'Invia Email - '}${emailStudent?.name}`}</DialogTitle></DialogHeader>
          {emailStep === 'compose' ? (
            <div className="space-y-4">
              {testMode ? (
                <div className="bg-purple-50 border border-purple-200 rounded-sm p-3 text-sm">
                  <div className="font-semibold text-purple-800 mb-1">🧪 Modalità Test</div>
                  <div className="text-xs text-purple-700">L&apos;email verrà inviata a <strong>{TEST_EMAIL}</strong> ma con i dati dell&apos;alunno <strong className="uppercase">{emailStudent?.name}</strong> (placeholder reali sostituiti).</div>
                </div>
              ) : (
              /* Recipients selection */
              <div className="space-y-2">
                <Label className="label-overline">Destinatari</Label>
                {(emailStudent?.emails || []).map((em, i) => em.address ? (
                  <label key={i} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#FAFAFA] p-1.5 rounded-sm">
                    <input type="checkbox" checked={emailForm.selectedRecipients.includes(em.address)} onChange={() => toggleRecipient(em.address)} className="rounded" />
                    <span className="text-xs text-[#A1A1AA] w-16">{em.label}</span>
                    <span>{em.address}</span>
                  </label>
                ) : null)}
                {/* Add new email inline */}
                <div className="flex items-center gap-2 pt-1 border-t border-dashed border-[#E4E4E7]">
                  <Select value={newSendEmail.label} onValueChange={v => setNewSendEmail({...newSendEmail, label: v})}>
                    <SelectTrigger className="w-[100px] rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{EMAIL_LABELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="email" value={newSendEmail.address} onChange={e => setNewSendEmail({...newSendEmail, address: e.target.value})} placeholder="Nuova email..." className="rounded-sm h-8 text-sm flex-1" data-testid="new-recipient-email" />
                  <Button type="button" size="sm" variant="outline" onClick={addEmailDuringSend} disabled={!newSendEmail.address.trim()} className="rounded-sm h-8 text-xs" data-testid="add-recipient-btn"><Plus size={14} /></Button>
                </div>
              </div>
              )}

              {!testMode && <div className="flex gap-2"><Button variant={emailMode === 'free' ? 'default' : 'outline'} size="sm" onClick={() => setEmailMode('free')} className="rounded-sm" data-testid="email-mode-free">Mail Libera</Button><Button variant={emailMode === 'template' ? 'default' : 'outline'} size="sm" onClick={() => setEmailMode('template')} className="rounded-sm" data-testid="email-mode-template">Da Modello</Button></div>}
              {emailMode === 'template' && (
                <div className="space-y-1"><Label className="label-overline">Seleziona Modello</Label><Select value={emailForm.template_id} onValueChange={handleTemplateChange}><SelectTrigger className="rounded-sm" data-testid="send-email-template-select"><SelectValue placeholder="Scegli modello..." /></SelectTrigger><SelectContent>{emailTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.name} {t.has_estratto_conto ? '(+ E.C.)' : ''} {t.has_iscrizione_attachments ? '(+ Regolamento+Listino)' : ''} {t.attachments?.length > 0 ? `(${t.attachments.length} all.)` : ''}</SelectItem>)}</SelectContent></Select></div>
              )}
              {emailFromPayments && emailSelectedPaymentIds.length > 0 && (
                <div className="flex items-center gap-2 p-2.5 bg-[#EEF2FF] border border-[#C7D2FE] rounded-sm text-xs" data-testid="estratto-conto-badge">
                  <FileText size={16} className="text-[#4338CA] shrink-0" />
                  <div className="flex-1">
                    <div className="font-semibold text-[#4338CA]">Estratto Conto pronto ({emailSelectedPaymentIds.length} movimenti)</div>
                    <div className="text-[10px] text-[#6366F1]">Scegli un modello con &quot;+ E.C.&quot; per allegare il PDF con solo i movimenti selezionati.</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setEmailSelectedPaymentIds([]); setEmailFromPayments(false); toast.info('Estratto conto rimosso'); }}
                    className="text-[#4338CA] hover:text-red-600 shrink-0"
                    title="Rimuovi estratto conto selettivo"
                    data-testid="remove-estratto-conto-btn"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
              {(emailMode === 'free' || (emailMode === 'template' && emailForm.template_id)) && (
                <div className="space-y-3">
                  {emailMode === 'template' && <p className="text-xs text-blue-700 bg-blue-50 p-2 rounded-sm">📝 Testo del modello con i dati di <strong className="uppercase">{emailStudent?.name}</strong> già inseriti. Puoi integrare/modificare prima dell&apos;invio.</p>}
                  <div className="space-y-1"><Label className="label-overline">Oggetto</Label><Input value={emailForm.subject} onChange={e => setEmailForm({...emailForm, subject: e.target.value})} placeholder="Oggetto email" className="rounded-sm" data-testid="send-email-subject" /></div>
                  <div className="space-y-1"><Label className="label-overline">Testo</Label>
                    <div className="border border-[#E4E4E7] rounded-sm bg-white" data-testid="send-email-body">
                      <ReactQuill value={emailForm.body || ''} onChange={(v) => setEmailForm({...emailForm, body: v})} modules={QUILL_MODULES_MAIL} theme="snow" style={{ minHeight: 220 }} />
                    </div>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label className="label-overline">Allegati aggiuntivi</Label>
                <input type="file" multiple onChange={e => setExtraFiles(Array.from(e.target.files))} className="text-sm" data-testid="extra-files-input" />
                {extraFiles.length > 0 && <div className="space-y-1">{extraFiles.map((f, i) => <div key={i} className="text-xs bg-[#F4F4F5] px-2 py-1 rounded flex items-center gap-1"><Paperclip size={12} />{f.name}</div>)}</div>}
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setShowEmailModal(false)} className="rounded-sm">Annulla</Button><Button onClick={() => setEmailStep('preview')} disabled={!emailForm.subject || !emailForm.body} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm" data-testid="send-email-preview-btn">Anteprima</Button></DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              {(() => { const sel=emailMode==='template'?emailTemplates.find(t=>t.id===emailForm.template_id):null; const subj=emailForm.subject; const body=emailForm.body; const hasEC=sel?.has_estratto_conto||false; const atts=sel?.attachments||[]; const hasAny=hasEC||atts.length>0||extraFiles.length>0; return (<>
                <div className="text-xs text-[#52525B] space-y-1 bg-[#F4F4F5] p-2 rounded-sm"><div><strong>A:</strong> {emailForm.selectedRecipients.join(', ')}</div><div><strong>Da:</strong> info@fiorimusicali.it</div></div>
                <div className="border border-[#E4E4E7] rounded-sm overflow-hidden"><div className="bg-[#2D2A4A] text-white text-center py-3 text-sm font-semibold">Fiori Musicali</div><div className="p-4"><div className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">Oggetto</div><div className="font-semibold text-sm mb-3">{subj}</div><div className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">Testo</div><div className="text-sm text-[#3F3F46] prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: body || '' }} /></div>
                  {hasAny&&(<div className="border-t border-[#E4E4E7] px-4 py-2 bg-[#FAFAFA]"><div className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">Allegati</div>{hasEC&&<div className="text-xs flex items-center gap-1 text-amber-700 font-medium"><FileText size={14} /> Estratto Conto PDF ({emailStudent?.name})</div>}{atts.map(a=><div key={a.id} className="text-xs flex items-center gap-1 text-[#52525B]"><Paperclip size={12} /> {a.filename}</div>)}{extraFiles.map((f,i)=><div key={`extra-${i}`} className="text-xs flex items-center gap-1 text-indigo-600"><Paperclip size={12} /> {f.name} <span className="text-[#A1A1AA]">(aggiuntivo)</span></div>)}</div>)}
                  <div className="bg-[#F4F4F5] text-center py-2 text-[10px] text-[#A1A1AA]">Fiori Musicali - Scuola di Musica</div></div>
              </>); })()}
              <DialogFooter><Button variant="outline" onClick={() => setEmailStep('compose')} className="rounded-sm" data-testid="send-email-back-btn">Modifica</Button><Button onClick={handleSendEmail} disabled={sendingEmail} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm" data-testid="send-email-confirm-btn">{sendingEmail ? 'Invio...' : <><PaperPlaneTilt size={16} className="mr-1" /> Conferma Invio</>}</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mass Email Modal */}
      <Dialog open={showMassEmail} onOpenChange={setShowMassEmail}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans']">Invio Email di Massa</DialogTitle>
            <DialogDescription>Invia email a {selectedIds.length} alunni selezionati</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label className="label-overline">Modello Email</Label>
              <Select value={massEmailForm.template_id} onValueChange={handleMassTemplateChange}>
                <SelectTrigger className="rounded-sm" data-testid="mass-email-template-select"><SelectValue placeholder="Scegli modello..." /></SelectTrigger>
                <SelectContent>{emailTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.name} {t.has_estratto_conto ? '(+ E.C.)' : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {massEmailForm.template_id && (
              <div className="space-y-3" data-testid="mass-email-editor">
                <p className="text-xs text-blue-700 bg-blue-50 p-2 rounded-sm">📝 Puoi modificare oggetto e testo. Usa <code className="bg-white px-1">{'{{nome_alunno}}'}</code>, <code className="bg-white px-1">{'{{riepilogo_lezioni}}'}</code>, <code className="bg-white px-1">{'{{conferma_lezione_link}}'}</code> ecc. — saranno sostituiti per ogni alunno.</p>
                <div className="space-y-1">
                  <Label className="label-overline">Oggetto</Label>
                  <Input value={massEditedSubject} onChange={e => { setMassEditedSubject(e.target.value); refreshMassPreview(e.target.value, massEditedBody, massEmailForm.template_id, selectedStudents); }} className="rounded-sm" data-testid="mass-email-subject" />
                </div>
                <div className="space-y-1">
                  <Label className="label-overline">Testo (con placeholder)</Label>
                  <div className="border border-[#E4E4E7] rounded-sm bg-white" data-testid="mass-email-body">
                    <ReactQuill value={massEditedBody || ''} onChange={(v) => { setMassEditedBody(v); refreshMassPreview(massEditedSubject, v, massEmailForm.template_id, selectedStudents); }} modules={QUILL_MODULES_MAIL} theme="snow" style={{ minHeight: 220 }} />
                  </div>
                </div>
              </div>
            )}
            {massPreview && (
              <div className="space-y-2" data-testid="mass-email-preview">
                <Label className="label-overline">Anteprima (esempio per {massPreview.sampleStudent.toUpperCase()})</Label>
                <p className="text-[10px] text-[#A1A1AA] italic">Ogni alunno riceverà il proprio testo con i suoi dati.</p>
                <div className="border border-[#E4E4E7] rounded-sm overflow-hidden">
                  <div className="bg-[#2D2A4A] text-white text-center py-2 text-xs font-semibold">Fiori Musicali</div>
                  <div className="p-3 max-h-[250px] overflow-y-auto">
                    <div className="text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1">Oggetto</div>
                    <div className="font-semibold text-sm mb-3">{massPreview.subject}</div>
                    <div className="text-[10px] text-[#A1A1AA] uppercase tracking-wider mb-1">Testo</div>
                    <div className="text-xs text-[#3F3F46] prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: massPreview.body || '' }} />
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-1">
              <Label className="label-overline">Destinatari ({selectedStudents.filter(s => s.emails?.some(em => em.address)).length} alunni con email)</Label>
              <div className="max-h-[200px] overflow-y-auto border border-[#E4E4E7] rounded-sm p-2 space-y-1">
                {selectedStudents.map(s => {
                  const emails = (s.emails || []).filter(em => em.address);
                  return (
                    <div key={s.id} className="flex items-center justify-between text-xs py-1 border-b border-[#F4F4F5] last:border-0">
                      <span className="font-medium uppercase">{s.name}</span>
                      <span className="text-[#52525B]">{emails.length > 0 ? emails.map(em => em.address).join(', ') : <span className="text-red-400">Nessuna email</span>}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMassEmail(false)} className="rounded-sm">Annulla</Button>
            <Button onClick={handleSendMassEmail} disabled={sendingMass || !massEmailForm.template_id} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm" data-testid="mass-email-send-btn">
              {sendingMass ? 'Invio in corso...' : <><PaperPlaneTilt size={16} className="mr-1" /> Invia a {selectedStudents.filter(s => s.emails?.some(em => em.address)).length} alunni</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Genera Presenze Modal - spostato dall'orario */}
      <Dialog open={showGenAttModal} onOpenChange={(o) => { setShowGenAttModal(o); if (!o) setGenAttStudent(null); }}>
        <DialogContent className="sm:max-w-3xl w-[90vw] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans'] flex items-center gap-2"><ClipboardText size={20} /> Genera Presenze</DialogTitle>
            <DialogDescription>
              Crea un record di presenza per ogni occorrenza settimanale delle lezioni selezionate nell&apos;intervallo indicato. Le presenze già esistenti vengono saltate; le festività italiane vengono marcate come &quot;Festivo&quot;.
            </DialogDescription>
          </DialogHeader>
          {genAttStudent && (
            <div className="bg-[#F4F4F5] border border-[#E4E4E7] rounded-sm p-3 text-sm">
              <div><span className="text-[#52525B]">Alunno:</span> <span className="font-semibold uppercase">{genAttStudent.name}</span></div>
              {(genAttStudent.attendance_count > 0) && (
                <div className="mt-1 text-xs text-[#52525B]">
                  Attualmente: <span className="font-semibold text-teal-700">{genAttStudent.lessons_with_attendance || 0}/{genAttStudent.lessons?.length || 0}</span> lezioni collegate · <span className="font-semibold text-teal-700">{genAttStudent.attendance_count}</span> record presenze
                </div>
              )}
            </div>
          )}
          <form onSubmit={handleSubmitGenAtt} className="space-y-4">
            <div>
              <Label className="label-overline mb-2 block">Lezioni</Label>
              {(genAttStudent?.lessons?.length || 0) === 0 ? (
                <div className="text-sm text-[#A1A1AA] italic border border-dashed border-[#E4E4E7] rounded-sm p-3 text-center">Nessuna lezione trovata per questo alunno</div>
              ) : (
                <div className="space-y-1 max-h-[220px] overflow-y-auto border border-[#E4E4E7] rounded-sm p-2 bg-white">
                  {(genAttStudent?.lessons || []).map(l => {
                    const checked = genAttSelectedLessonIds.includes(l.id);
                    return (
                      <label key={l.id} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs transition-colors ${checked ? 'bg-teal-50' : 'hover:bg-[#F4F4F5]'}`} data-testid={`gen-att-lesson-row-${l.id}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleGenAttLesson(l.id)}
                          className="rounded"
                          data-testid={`gen-att-lesson-${l.id}`}
                        />
                        <span className="font-semibold uppercase flex-1">{l.teacher_name || '—'}</span>
                        <span className="text-[#52525B]">{DAY_NAMES[l.day] || '?'} {l.start_time}-{l.end_time}</span>
                        {l.is_prova && <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded uppercase">Prova</span>}
                      </label>
                    );
                  })}
                </div>
              )}
              {(genAttStudent?.lessons?.length || 0) > 0 && (
                <div className="flex gap-2 mt-2">
                  <button type="button" onClick={() => setGenAttSelectedLessonIds((genAttStudent?.lessons || []).map(l => l.id))} className="text-[10px] uppercase tracking-wider text-[#2D2A4A] hover:underline" data-testid="gen-att-select-all">Seleziona tutte</button>
                  <span className="text-[10px] text-[#A1A1AA]">·</span>
                  <button type="button" onClick={() => setGenAttSelectedLessonIds([])} className="text-[10px] uppercase tracking-wider text-[#52525B] hover:underline" data-testid="gen-att-deselect-all">Deseleziona</button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="label-overline">Dal</Label>
                <Input type="date" value={genAttForm.date_from} onChange={(e) => setGenAttForm({ ...genAttForm, date_from: e.target.value })} className="rounded-sm" data-testid="gen-att-date-from" required />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Al</Label>
                <Input type="date" value={genAttForm.date_to} onChange={(e) => setGenAttForm({ ...genAttForm, date_to: e.target.value })} className="rounded-sm" data-testid="gen-att-date-to" required />
              </div>
            </div>
            {/* Anteprima festività read-only nel range selezionato (gestione in "Calendario Festività") */}
            <div className="border border-[#E4E4E7] rounded-sm p-3 bg-[#FAFAFA] space-y-2" data-testid="gen-att-holidays-preview">
              <div className="flex items-center justify-between">
                <Label className="label-overline">Festività (saltate come "Festivo")</Label>
                <span className="text-[9px] uppercase tracking-wider text-[#A1A1AA]">Gestisci in Didattica → Calendario Festività</span>
              </div>
              {genAttHolidaysLoading ? (
                <div className="text-xs text-[#A1A1AA]">Caricamento festività...</div>
              ) : (() => {
                const inRange = (iso) => iso >= genAttForm.date_from && iso <= genAttForm.date_to;
                const custom = (genAttHolidays.custom || []).filter(h => inRange(h.date));
                const national = (genAttHolidays.national || []).filter(h => inRange(h.date));
                if (custom.length === 0 && national.length === 0) {
                  return <div className="text-xs text-[#A1A1AA] italic">Nessuna festività nel range selezionato.</div>;
                }
                return (
                  <div className="max-h-[140px] overflow-y-auto text-[11px] space-y-0.5">
                    {custom.map(h => (
                      <div key={h.id} className="flex items-center gap-2 px-1 py-0.5" data-testid={`holiday-row-${h.id}`}>
                        <span className="font-mono font-semibold text-[#2D2A4A]">{h.date}</span>
                        <span className="text-[#52525B] truncate flex-1">{h.label}</span>
                        {h.source === 'pdf_import' && <span className="text-[9px] uppercase tracking-wider text-teal-700 bg-teal-50 px-1 rounded">PDF</span>}
                      </div>
                    ))}
                    {national.length > 0 && (
                      <>
                        <div className="text-[9px] uppercase tracking-wider text-[#A1A1AA] mt-1 pt-1 border-t border-[#E4E4E7]">Nazionali (automatiche)</div>
                        {national.map(h => (
                          <div key={h.id} className="flex items-center gap-2 px-1 py-0.5 text-[#A1A1AA]">
                            <span className="font-mono">{h.date}</span>
                            <span className="italic">{h.label}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
            {/* Preview conteggio presenze - box evidenziato dentro il modal (prima del footer) */}
            {genAttPreview && genAttPreview.total > 0 && (
              <div className="rounded-sm border-2 border-[#4338CA] bg-gradient-to-br from-[#EEF2FF] to-white p-3" data-testid="gen-att-preview">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#4338CA] font-semibold">Anteprima Generazione</div>
                    <div className="text-[#2D2A4A] mt-0.5">
                      Verranno create <span className="text-[#4338CA] text-2xl font-['Work_Sans'] font-extrabold">{genAttPreview.total}</span> presenze
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-0.5 text-xs">
                    <span className="flex items-center gap-1 text-teal-700"><span className="inline-block w-2 h-2 rounded-full bg-teal-600"></span><span className="font-['Work_Sans'] font-bold">{genAttPreview.active}</span> attive</span>
                    {genAttPreview.holiday > 0 && (
                      <span className="flex items-center gap-1 text-amber-700"><span className="inline-block w-2 h-2 rounded-full bg-amber-500"></span><span className="font-['Work_Sans'] font-bold">{genAttPreview.holiday}</span> festive</span>
                    )}
                  </div>
                </div>
                {genAttPreview.perLesson.length > 1 && (
                  <details className="mt-2 cursor-pointer">
                    <summary className="text-[10px] uppercase tracking-wider text-[#4338CA] hover:text-[#2D2A4A]">Dettaglio per lezione</summary>
                    <div className="mt-1 space-y-0.5 pl-2">
                      {genAttPreview.perLesson.map(pl => (
                        <div key={pl.id} className="text-[10px] text-[#52525B]" data-testid={`gen-att-preview-lesson-${pl.id}`}>
                          <span className="font-semibold uppercase">{pl.teacher_name}</span> {DAY_NAMES[pl.day]} {pl.start_time}: <span className="font-['Work_Sans']">{pl.total}</span> ({pl.active}A{pl.holiday ? ` + ${pl.holiday}F` : ''})
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowGenAttModal(false)} className="rounded-sm">Annulla</Button>
              <Button type="submit" disabled={genAttLoading || genAttSelectedLessonIds.length === 0} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm" data-testid="gen-att-submit-btn">
                {genAttLoading ? 'Creazione...' : (genAttPreview && genAttPreview.total > 0 ? `Crea ${genAttPreview.total} Presenze` : `Crea Presenze (${genAttSelectedLessonIds.length})`)}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assegna Orario Modal - griglia stile prenotazione pubblica */}
      <Dialog open={showAssignSchedule} onOpenChange={(o) => { setShowAssignSchedule(o); if (!o) setAssignStudent(null); }}>
        <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans'] flex items-center gap-2"><CalendarPlus size={20} /> Assegna Orario {anno ? `(${anno})` : ''}</DialogTitle>
            <DialogDescription>
              Crea una prenotazione per {assignStudent ? <strong className="uppercase">{assignStudent.name}</strong> : "l&apos;alunno"} come se venisse compilata dalla homepage.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Step 1: teacher */}
            <div className="space-y-2">
              <Label className="label-overline">1. Insegnante</Label>
              <Select value={assignForm.teacher_id} onValueChange={handleAssignTeacherChange}>
                <SelectTrigger className="rounded-sm" data-testid="assign-schedule-teacher-select"><SelectValue placeholder="Seleziona insegnante..." /></SelectTrigger>
                <SelectContent>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name?.toUpperCase()}{t.instrument ? ` — ${t.instrument}` : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {assignForm.teacher_id && (
              <>
                {/* Step 2: durata + prova */}
                <div className="space-y-2">
                  <Label className="label-overline">2. Durata lezione</Label>
                  <div className="flex flex-wrap gap-2">
                    {[30, 45, 60].map(d => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setAssignForm(prev => ({ ...prev, durata: d, is_prova: false, giorno: -1, ora_inizio: '' }))}
                        className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${assignForm.durata === d && !assignForm.is_prova ? 'bg-[#2D2A4A] text-white border-[#2D2A4A]' : 'bg-white text-[#52525B] border-[#E4E4E7] hover:bg-[#F4F4F5]'}`}
                        data-testid={`assign-schedule-durata-${d}`}
                      >{d} min</button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setAssignForm(prev => ({ ...prev, durata: 30, is_prova: true, giorno: -1, ora_inizio: '' }))}
                      className={`px-3 py-1.5 rounded-sm text-xs font-bold border transition-colors uppercase tracking-wider ${assignForm.is_prova ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-400 hover:bg-amber-50'}`}
                      data-testid="assign-schedule-prova"
                    >Prova (30 min)</button>
                  </div>
                </div>

                {/* Step 3: grid */}
                <div className="space-y-2">
                  <Label className="label-overline">3. Scegli lo slot</Label>
                  {assignLoadingSlots ? (
                    <div className="text-center py-8 text-[#52525B] text-sm">Caricamento disponibilità...</div>
                  ) : assignAvailability.length === 0 ? (
                    <div className="text-center py-8 text-[#71717A] text-xs">Nessuna disponibilità caricata</div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 flex-wrap text-[10px] text-[#52525B]">
                        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-white border border-[#E4E4E7] rounded-sm" /> Libero</div>
                        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-slate-100 border border-slate-300 rounded-sm" /> Non prenot.</div>
                        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-amber-100 border border-amber-300 rounded-sm" /> In attesa</div>
                        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-orange-100 border border-orange-300 rounded-sm" /> Prova</div>
                        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-red-200 border border-red-400 rounded-sm" /> Pagato</div>
                        <div className="flex items-center gap-1"><div className="w-3 h-3 bg-[#2D2A4A] rounded-sm" /> Selezionato</div>
                      </div>
                      <div className="overflow-x-auto border border-[#E4E4E7] rounded-sm p-1 bg-[#FAFAFA]">
                        <div className="grid grid-cols-5 gap-1 min-w-[600px]">
                          {['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì'].map((day, dayIdx) => (
                            <div key={dayIdx} className="text-center">
                              <div className="text-xs font-semibold text-[#52525B] py-1.5 bg-[#F4F4F5] rounded-t-sm">{day}</div>
                              <div className="space-y-px bg-[#F4F4F5] rounded-b-sm p-px">
                                {assignAvailability[dayIdx]?.slots.map((slot, slotIdx) => {
                                  const slotsNeeded = assignForm.durata / 15;
                                  const fits = assignCanSlotFit(assignAvailability[dayIdx].slots, slotIdx, slotsNeeded);
                                  const isSelected = assignForm.giorno === dayIdx && assignForm.ora_inizio === slot.time;
                                  const isInRange = assignForm.giorno === dayIdx && assignForm.ora_inizio && (() => {
                                    const [sh, sm] = assignForm.ora_inizio.split(':').map(Number);
                                    const [ch, cm] = slot.time.split(':').map(Number);
                                    const startMin = sh * 60 + sm;
                                    const curMin = ch * 60 + cm;
                                    return curMin >= startMin && curMin < startMin + assignForm.durata;
                                  })();
                                  if (!slot.available) {
                                    const bs = slot.booking_stato;
                                    const flags = new Set(slot.lesson_flags || []);
                                    const isDisabled = slot.is_disabled && !bs && flags.size === 0;
                                    const isPaid = flags.has('r1');
                                    const isProvaPending = !isDisabled && !isPaid && bs && slot.is_prova;
                                    const cls = isDisabled ? 'bg-slate-100 text-slate-600 border-slate-300'
                                      : isPaid ? 'bg-red-200 text-red-800 border-red-400'
                                      : isProvaPending ? 'bg-orange-100 text-orange-700 border-orange-300'
                                      : 'bg-amber-100 text-amber-700 border-amber-300';
                                    return (<div key={slot.time} className={`h-6 border ${cls} text-[9px] flex items-center justify-center leading-none`}>{slot.time}</div>);
                                  }
                                  return (
                                    <button
                                      key={slot.time}
                                      type="button"
                                      onClick={() => fits && handleAssignSlotClick(dayIdx, slot.time)}
                                      disabled={!fits}
                                      title={fits ? `${slot.time} - ${assignForm.durata} min` : `Non c'è spazio consecutivo per ${assignForm.durata} min`}
                                      className={`w-full h-6 text-[10px] font-medium transition-colors ${isSelected || isInRange ? 'bg-[#2D2A4A] text-white' : fits ? 'bg-white hover:bg-green-50 text-[#52525B]' : 'bg-white text-[#D4D4D8] cursor-not-allowed'}`}
                                      data-testid={`assign-slot-${dayIdx}-${slot.time}`}
                                    >{slot.time}</button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {assignForm.giorno >= 0 && assignForm.ora_inizio && (
                  <div className="text-xs text-[#3F3F46] bg-blue-50 border border-blue-200 rounded-sm p-2" data-testid="assign-schedule-summary">
                    Selezionato: <strong>{PLANNER_DAYS[assignForm.giorno]}</strong> alle <strong>{assignForm.ora_inizio}</strong> per <strong>{assignForm.durata} min</strong>
                    {assignForm.is_prova && <span className="ml-2 inline-block px-2 py-0.5 bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wide rounded">Prova</span>}
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowAssignSchedule(false)} className="rounded-sm">Annulla</Button>
            <Button
              type="button"
              disabled={assignSaving || !assignForm.teacher_id || assignForm.giorno < 0 || !assignForm.ora_inizio}
              onClick={handleSaveAssignSchedule}
              className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm"
              data-testid="assign-schedule-save-btn"
            >
              {assignSaving ? 'Salvataggio...' : <><CalendarPlus size={16} className="mr-1" />Crea Prenotazione</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking invite modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="rounded-sm sm:max-w-[560px]" data-testid="booking-invite-modal">
          <DialogHeader>
            <DialogTitle>Link Prenotazione &mdash; {bookingStudent?.name?.toUpperCase()}</DialogTitle>
            <DialogDescription>
              Link univoco che apre la pagina di prenotazione con i dati dell&apos;alunno già pre-compilati.
              L&apos;alunno potrà scegliere strumento, insegnante e orario tra quelli ancora disponibili.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {bookingLoading ? (
              <div className="text-center py-6 text-[#52525B] text-sm">Generazione link in corso...</div>
            ) : (
              <>
                <div className="space-y-3">
                  <Label className="label-overline">
                    {bookingLinks.length > 1 ? `Link personalizzati (${bookingLinks.length} - uno per insegnante)` : 'Link Personalizzato'}
                  </Label>
                  {bookingLinks.length > 0 ? bookingLinks.map((lk, idx) => (
                    <div key={idx} className="border border-[#E4E4E7] rounded-sm p-2 space-y-2 bg-[#FAFAFA]" data-testid={`booking-link-${idx}`}>
                      {lk.teacher_name && (
                        <div className="text-[10px] font-bold uppercase tracking-wider text-[#2D2A4A]">
                          {lk.teacher_name}{lk.instrument ? <span className="text-[#71717A] font-medium"> ({lk.instrument})</span> : null}
                        </div>
                      )}
                      <div className="flex items-stretch gap-2">
                        <Input readOnly value={lk.url} className="rounded-sm text-xs font-mono flex-1 bg-white" data-testid={`booking-invite-url-${idx}`} />
                        <Button type="button" variant="outline" onClick={() => copyUrl(lk.url)} className="rounded-sm h-9 shrink-0" data-testid={`copy-booking-url-btn-${idx}`}>
                          <Copy size={16} className="mr-1" />Copia
                        </Button>
                      </div>
                    </div>
                  )) : (
                    <div className="flex items-stretch gap-2">
                      <Input readOnly value={bookingUrl} className="rounded-sm text-xs font-mono flex-1 bg-[#FAFAFA]" data-testid="booking-invite-url" />
                      <Button type="button" variant="outline" onClick={() => copyUrl(bookingUrl)} className="rounded-sm h-9 shrink-0" data-testid="copy-booking-url-btn">
                        <Copy size={16} className="mr-1" />Copia
                      </Button>
                    </div>
                  )}
                </div>
                {bookingStudent && (() => {
                  const recips = (bookingStudent.emails || []).filter(em => em.address).map(em => em.address);
                  return (
                    <div className="bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm p-3 text-xs" data-testid="booking-invite-recipients">
                      <div className="text-[#52525B] uppercase font-semibold tracking-wider text-[10px] mb-1">Verrà inviata a:</div>
                      {recips.length > 0
                        ? <div className="space-y-0.5 font-mono text-[#2D2A4A]">{recips.map((r, i) => <div key={i}>{r}</div>)}</div>
                        : <div className="text-red-600">Nessun indirizzo email in anagrafica</div>}
                      {bookingLinks.length > 1 && recips.length > 0 && (
                        <div className="text-[#52525B] italic mt-2">Verranno inviate <strong>{bookingLinks.length} email separate</strong>, una per insegnante.</div>
                      )}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowBookingModal(false)} className="rounded-sm">Chiudi</Button>
            <Button
              type="button"
              onClick={sendBookingInvite}
              disabled={bookingSending || bookingLoading || !bookingStudent?.emails?.some(em => em.address)}
              className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm"
              data-testid="send-booking-invite-btn"
            >
              <PaperPlaneTilt size={16} className="mr-1" />{bookingSending ? 'Invio...' : 'Invia Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Mail Conferma Inizio Lezioni */}
      <Dialog open={showLessonStartModal} onOpenChange={(o) => { setShowLessonStartModal(o); if (!o) setLsPreview(null); }}>
        <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="lesson-start-modal">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans'] flex items-center gap-2"><PaperPlaneTilt size={20} />Conferma Inizio Lezioni {(anno || getLsAnno()) ? `— A.A. ${anno || getLsAnno()}/${String((anno || getLsAnno()) + 1).slice(-2)}` : ''}</DialogTitle>
            <DialogDescription>
              Invia una mail personalizzata a <strong>{selectedIds.length}</strong> alunni selezionati. La mail include automaticamente <strong>solo le lezioni dell&apos;A.A. {(anno || getLsAnno()) || '?'}/{(anno || getLsAnno()) ? String((anno || getLsAnno()) + 1).slice(-2) : '?'}</strong> di ciascun alunno.
              {!anno && lsForm.start_date && <span className="block mt-1 text-[10px] text-amber-600">Anno derivato dalla data di inizio (l'anno non è selezionato nell'header dashboard).</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="label-overline">Data inizio lezioni (Lunedì)</Label>
              <Input type="date" value={lsForm.start_date} onChange={(e) => setLsForm({ ...lsForm, start_date: e.target.value })} className="rounded-sm" data-testid="lesson-start-date" />
            </div>
            <div className="space-y-1 bg-purple-50 border border-purple-200 rounded-sm p-3">
              <Label className="label-overline text-purple-900">Invio di prova (opzionale)</Label>
              <Input
                type="email"
                value={lsForm.test_email}
                onChange={(e) => setLsForm({ ...lsForm, test_email: e.target.value })}
                placeholder="es. tuaemail@fiorimusicali.it — se compilata, tutte le mail vanno qui"
                className="rounded-sm"
                data-testid="lesson-start-test-email"
              />
              {lsForm.test_email && <p className="text-[10px] text-purple-700 mt-1">⚠️ Tutte le {selectedIds.length} mail andranno a <strong>{lsForm.test_email}</strong> invece che agli alunni.</p>}
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={refreshLessonStartPreview} className="rounded-sm h-8 text-xs" data-testid="lesson-start-preview-btn">
                <Eye size={14} className="mr-1" />Aggiorna anteprima
              </Button>
              <p className="text-[10px] text-[#71717A]">La mail contiene solo l'annuncio dell'inizio lezioni con giorno/ora/insegnante. Nessun testo condizionale.</p>
            </div>
            {lsPreview && (
              <div className="border border-[#E4E4E7] rounded-sm bg-white overflow-hidden">
                <div className="bg-[#FAFAFA] px-3 py-2 text-xs text-[#52525B] border-b border-[#E4E4E7]">
                  <strong>Anteprima per:</strong> {lsPreview.student_name} · {lsPreview.lessons_count} lezioni · <strong>Oggetto:</strong> {lsPreview.subject}
                </div>
                <div className="p-2 max-h-[380px] overflow-y-auto" dangerouslySetInnerHTML={{ __html: lsPreview.html }} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowLessonStartModal(false)} className="rounded-sm">Annulla</Button>
            <Button type="button" onClick={handleSendLessonStart} disabled={lsSending || !selectedIds.length || !lsForm.start_date} className={`rounded-sm ${lsForm.test_email ? 'bg-purple-600 hover:bg-purple-700' : 'bg-emerald-600 hover:bg-emerald-700'}`} data-testid="lesson-start-send-btn">
              <PaperPlaneTilt size={16} className="mr-1" />
              {lsSending ? 'Invio...' : lsForm.test_email ? `Invio di Prova a ${lsForm.test_email}` : `Invia a ${selectedIds.length} alunni`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tariffe per Lezione Modal */}
      <Dialog open={!!tariffeStudent} onOpenChange={(o) => { if (!o) { setTariffeStudent(null); if (students.length) loadStudentsListinoStatus(students); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans']">Tariffe per Lezione — {tariffeStudent?.name?.toUpperCase()}{anno ? ` (A.A. ${anno + 1}/${String(anno + 2).slice(2)})` : ''}</DialogTitle>
            <DialogDescription>
              Assegna un listino dell&apos;anno successivo a ciascuna lezione. La <strong>quota associativa</strong> verrà inclusa una sola volta nell&apos;email (sulla prima lezione).
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {tariffeLoading ? (
              <p className="text-sm text-[#52525B] text-center py-4">Caricamento...</p>
            ) : tariffeLessons.length === 0 ? (
              <p className="text-sm text-[#52525B] text-center py-4">Nessuna lezione trovata per l&apos;anno {anno}.</p>
            ) : (
              tariffeLessons.map(l => {
                const DAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
                let dur = '';
                try {
                  const [sh, sm] = (l.start_time || '0:0').split(':').map(Number);
                  const [eh, em] = (l.end_time || '0:0').split(':').map(Number);
                  const d = (eh * 60 + em) - (sh * 60 + sm);
                  if (d > 0) dur = ` · ${d} min`;
                } catch { /* ignore */ }
                return (
                  <div key={l.id} className="border border-[#E4E4E7] rounded-sm p-3 space-y-2" data-testid={`tariffa-lesson-${l.id}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{DAYS[l.day]} {l.start_time}-{l.end_time}<span className="text-[#71717A]">{dur}</span></span>
                      <span className="uppercase text-xs text-[#52525B]">{l.teacher_name}</span>
                    </div>
                    <select
                      value={l.listino_id || ''}
                      onChange={(e) => updateTariffaLesson(l.id, e.target.value)}
                      className="w-full rounded-sm border border-[#E4E4E7] px-2 py-1 text-sm"
                      data-testid={`tariffa-select-${l.id}`}
                    >
                      <option value="">— Seleziona listino —</option>
                      {tariffeAllListini.map(lst => (
                        <option key={lst.id} value={lst.id}>{lst.codice}{lst.descrizione ? ' - ' + lst.descrizione : ''}{lst.prezzo_rata ? ' (' + lst.prezzo_rata + '€)' : ''}</option>
                      ))}
                    </select>
                  </div>
                );
              })
            )}
          </div>
          <DialogFooter>
            <Button type="button" onClick={() => { setTariffeStudent(null); if (students.length) loadStudentsListinoStatus(students); }} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm">Fatto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
}
